/**
 * Angel Ashes — Arrival / transport / calendar / return choices
 * Split from booking-wizard.js for Shopify Theme Editor's 256 KB asset limit.
 * Loaded before booking-wizard.js from sections/booking-wizard.liquid.
 */
(function (global) {
  'use strict'

  function create(deps) {
    deps = deps || {}
    var escapeHtml = deps.escapeHtml
    var stepIntroHtml = deps.stepIntroHtml || function (headline, helperHtml, headlineClass) {
      var extra = headlineClass ? ' ' + String(headlineClass).trim() : ''
      var helper = helperHtml ? String(helperHtml) : ''
      return (
        '<div class="baw-step-intro">' +
        '<h2 class="baw-headline baw-wizard-title' + extra + '">' + escapeHtml(headline) + '</h2>' +
        helper +
        '</div>'
      )
    }
    var optionalSkipButtonHtml = deps.optionalSkipButtonHtml
    var formatMoney = deps.formatMoney
    var firstAvailableVariant = deps.firstAvailableVariant
    var isLonsdaleTransport = deps.isLonsdaleTransport
    var isVetTransport = deps.isVetTransport
    var isPriorityCollectionSelected = deps.isPriorityCollectionSelected
    var lonsdaleGoodbyeMinutes = deps.lonsdaleGoodbyeMinutes
    var collectionTimeframeForState = deps.collectionTimeframeForState
    var timeWindowOptionsForState = deps.timeWindowOptionsForState
    var timeWindowHelperForState = deps.timeWindowHelperForState
    var buildArrivalTimeSlotsHtml = deps.buildArrivalTimeSlotsHtml
    var lonsdaleHoursHelperText = deps.lonsdaleHoursHelperText
    var lonsdaleArrivalFooterNotesHtml = deps.lonsdaleArrivalFooterNotesHtml
    var pickupCollectionFooterNotesHtml = deps.pickupCollectionFooterNotesHtml
    var afterHoursCollectionSmsHelper = deps.afterHoursCollectionSmsHelper
    var collectionWindowSmsHelper = deps.collectionWindowSmsHelper
    var lonsdaleArrivalSmsHelper = deps.lonsdaleArrivalSmsHelper
    var vetPriorityDateHintHtml = deps.vetPriorityDateHintHtml
    var wrapGuidanceBarHtml = deps.wrapGuidanceBarHtml
    var goodbyeRoomVisualHtml = deps.goodbyeRoomVisualHtml
    var cremationProductForType = deps.cremationProductForType
    var needsManualWeightPick = deps.needsManualWeightPick
    var suggestedVariant = deps.suggestedVariant
    var suggestedVariantId = deps.suggestedVariantId
    var parseWeightRange = deps.parseWeightRange
    var weightInRange = deps.weightInRange
    var enhanceOptionGroup = deps.enhanceOptionGroup
    var enhanceCalendarKeyboard = deps.enhanceCalendarKeyboard
    var stepCopyText = deps.stepCopyText
    var possessiveName = deps.possessiveName
    var isUsablePetName = deps.isUsablePetName
    var formatCremationTimingDisplay = deps.formatCremationTimingDisplay
    var cremationReadyWithinLabel = deps.cremationReadyWithinLabel
    var getMemorialCatalog = deps.getMemorialCatalog
    var getMemorialEntry = deps.getMemorialEntry
    var memorialAddonStepId = deps.memorialAddonStepId
    var resolveMemorialItemAddonFlags = deps.resolveMemorialItemAddonFlags
    var isMemorialMultiSelectMode = deps.isMemorialMultiSelectMode
    var isMemorialRequiredSelectionMode = deps.isMemorialRequiredSelectionMode

  /* ---------------------------- step sequence ------------------------------ */

  // Family-facing intake copy. Internal id `lonsdale` / productKey stay unchanged for ops.
  var LONSDALE_FAMILY_LABEL = 'Bring to Lonsdale'
  var LONSDALE_PRODUCT_DISPLAY_TITLE = 'Goodbye Room Time'
  var LONSDALE_SERVICE_HEADLINE = 'How long would you like with {pet}?'

  var TRANSPORT_CATEGORY_DEFS = [
    {
      id: 'lonsdale',
      label: LONSDALE_FAMILY_LABEL,
      productKey: 'transportLonsdale',
      // Quiet method-tile line — Theme Editor `lonsdale_hours_helper` overrides at runtime.
      tileDesc: 'Weekdays · 10am–1pm',
      requires_date: true,
      requires_time_window: true,
      requires_vet_clinic: false,
      requires_address_choice: false,
      serviceHeadline: LONSDALE_SERVICE_HEADLINE,
      // Default; Theme Editor `lonsdale_details_headline` overrides via config at runtime.
      detailsHeadline: 'When would you prefer to arrive?',
      dateLabel: 'Drop-off date',
      timeWindowLabel: 'Preferred arrival time',
    },
    {
      id: 'vet',
      label: 'Vet Collection',
      productKey: 'transportVet',
      tileDesc: 'We collect from your clinic',
      requires_date: true,
      requires_time_window: false,
      requires_vet_clinic: true,
      requires_address_choice: false,
      serviceHeadline: 'How would you like us to collect {pet}?',
      detailsHeadline: 'When will {pet} be ready for collection?',
      dateLabel: 'Ready for collection from',
    },
    {
      id: 'home',
      label: 'Home Collection',
      productKey: 'transportHome',
      tileDesc: 'We come to you',
      requires_date: true,
      requires_time_window: false,
      requires_vet_clinic: false,
      requires_address_choice: true,
      serviceHeadline: 'Choose your collection service',
      noteKey: 'home',
      defaultSmsNote: 'We\u2019ll SMS you to confirm your requested date and arrange a suitable time.',
      detailsHeadline: 'When and where should we collect {pet}?',
      dateLabel: 'Preferred collection date',
    },
  ]

  function transportCategoryDef(id) {
    return TRANSPORT_CATEGORY_DEFS.find(function (c) { return c.id === id }) || null
  }

  function variantDisplayLabel(variant, productTitle) {
    var label = variant && variant.title ? variant.title : ''
    if (!label || /^default title$/i.test(label)) return productTitle || 'Included'
    return label
  }

  function mapLonsdaleGoodbyeDisplay(label, opts) {
    opts = opts || {}
    var raw = String(label || '').replace(/\s+/g, ' ').trim()
    if (!raw) return null
    var s = raw.toLowerCase()
    var force = !!opts.forceLonsdale

    var minsMatch = s.match(/\b(60|30|5)\s*(?:min|mins|minutes)?\b/)
    var mins = minsMatch ? Number(minsMatch[1]) : null
    if (mins == null) {
      if (/\bextended\b/.test(s)) mins = 60
      else if (/\bshort\b/.test(s)) mins = 5
      else if (/\bprivate\b/.test(s)) mins = 30
    }

    var looksLike =
      force ||
      /\b(goodbye|private\s*time|short(\s+goodbye)?|extended(\s+goodbye)?|handover)\b/i.test(s) ||
      (mins != null && /\b(short|private|extended|goodbye)\b/i.test(s))

    if (!looksLike || mins == null) return null
    if (mins === 5) return { title: 'Short Goodbye', desc: '5 min' }
    if (mins === 30) return { title: 'Private Goodbye', desc: '30 min' }
    if (mins === 60) return { title: 'Extended Goodbye', desc: '60 min' }
    return null
  }

  function lonsdaleGoodbyeOptionLabel(rawLabel) {
    var mapped = mapLonsdaleGoodbyeDisplay(rawLabel, { forceLonsdale: true })
    if (!mapped) return rawLabel
    return mapped.desc ? mapped.title + ' · ' + mapped.desc : mapped.title
  }

  /** Variant / option label cues for Same-Day (After Hours) Collection — home only; never offered for vet. */

  function isAfterHoursOptionLabel(label) {
    return /after\s*-?\s*hours?/i.test(String(label || ''))
  }

  /** Variant / option label cues for Vet Priority Collection (same as isPriorityCollectionSelected). */

  function isPriorityOptionLabel(label) {
    var s = String(label || '')
    return (
      /\b(priority|express|urgent)\b/i.test(s) ||
      /\b(?:within\s+)?48\s*(?:hrs?|hours?)\b/i.test(s)
    )
  }

  /** Find Same-Day (After Hours) service option for the current transport category. */

  function findAfterHoursTransportOption(config, state) {
    if (!state || !state.transport) return null
    var cat = findTransportCategory(config, state.transport.categoryId)
    var options = cat ? cat.options : getTransportMainOptions(config)
    return (options || []).find(function (o) {
      return isAfterHoursOptionLabel((o && o.label) || '')
    }) || null
  }

  /** Find Priority service option for the current transport category (vet). */

  function findPriorityTransportOption(config, state) {
    if (!state || !state.transport) return null
    var cat = findTransportCategory(config, state.transport.categoryId)
    var options = cat ? cat.options : getTransportMainOptions(config)
    return (options || []).find(function (o) {
      return isPriorityOptionLabel((o && o.label) || '')
    }) || null
  }

  function serviceOptionsFromProduct(product, category) {
    if (!product || !Array.isArray(product.variants) || !product.variants.length) return []
    var title =
      category.id === 'lonsdale'
        ? LONSDALE_PRODUCT_DISPLAY_TITLE
        : product.title || category.label
    return product.variants
      .filter(function (v) {
        if (!v || v.available === false) return false
        // Vets are closed evenings/weekends — After Hours is home-only even if the
        // Shopify vet product still has an after-hours variant.
        if (category.id === 'vet' && isAfterHoursOptionLabel(variantDisplayLabel(v, title))) {
          return false
        }
        return true
      })
      .map(function (v) {
        var rawLabel = variantDisplayLabel(v, title)
        return {
          key: category.id + ':variant:' + v.id,
          label: category.id === 'lonsdale' ? lonsdaleGoodbyeOptionLabel(rawLabel) : rawLabel,
          variant: v,
          categoryId: category.id,
          categoryLabel: category.label,
          requires_date: !!category.requires_date,
          requires_time_window: !!category.requires_time_window,
          requires_vet_clinic: !!category.requires_vet_clinic,
          requires_address_choice: !!category.requires_address_choice,
          sms_note: '',
          source: 'category-variant',
        }
      })
  }

  function isLegacyLonsdaleDropOffLabel(label) {
    var s = String(label || '').trim()
    if (!s) return true
    if (/handover|drop\s*-?\s*off/i.test(s)) return true
    if (/^lonsdale$/i.test(s)) return true
    return false
  }

  /** Customer-facing path title; maps older “Drop Off / Handover” theme defaults. */

  function normalizeLonsdaleFamilyLabel(label) {
    if (isLegacyLonsdaleDropOffLabel(label)) return LONSDALE_FAMILY_LABEL
    return String(label || '').trim() || LONSDALE_FAMILY_LABEL
  }

  function transportCategoryLabel(config, def) {
    var custom = config.transportLabels && config.transportLabels[def.id]
    var label = (custom && String(custom).trim()) || def.label
    if (def.id === 'lonsdale') return normalizeLonsdaleFamilyLabel(label)
    return label
  }

  function familyFacingTransportCategoryLabel(state) {
    if (!state || !state.transport) return null
    if (state.transport.categoryId === 'lonsdale' || isLonsdaleTransport(state)) {
      return normalizeLonsdaleFamilyLabel(state.transport.categoryLabel)
    }
    var raw = state.transport.categoryLabel
    if (raw && /lonsdale/i.test(raw) && /handover|drop\s*-?\s*off/i.test(raw)) {
      return LONSDALE_FAMILY_LABEL
    }
    return raw || null
  }

  function lonsdaleServiceSummaryLabel(state) {
    if (!state || !state.transport) return null
    var label = state.transport.label
    if (!label || label === state.transport.categoryLabel) return null
    var parts = formatTransportTileParts(label)
    if (parts.desc && /^\d+\s*min$/i.test(String(parts.desc))) return parts.desc
    var mapped = mapLonsdaleGoodbyeDisplay(label, { forceLonsdale: true })
    if (mapped && mapped.desc) return mapped.desc
    if (parts.desc) return parts.desc
    return parts.title || compactTransportChoice(label) || null
  }

  function resolveTransportSmsNote(def, notes) {
    if (!def.noteKey) return ''
    var note = String((notes && notes[def.noteKey]) || '').trim()
    // Replace legacy home SMS copy so theme-setting leftovers don't keep the old wording
    if (
      def.id === 'home' &&
      (!note ||
        /gentle collection window|text you to confirm|arrange a convenient collection time|collection window and pickup time/i.test(
          note
        ))
    ) {
      return def.defaultSmsNote || note
    }
    return note || def.defaultSmsNote || ''
  }

  function buildTransportCategories(config) {
    var products = config.products || {}
    var notes = config.transportNotes || {}
    return TRANSPORT_CATEGORY_DEFS.map(function (def) {
      var product = products[def.productKey] || null
      if (!product) return null
      var label = transportCategoryLabel(config, def)
      var category = Object.assign({}, def, { label: label })
      var options = serviceOptionsFromProduct(product, category)
      // Category with a product but no sellable variants still appears (included path)
      if (!options.length && product) {
        options = [{
          key: def.id + ':base',
          label: label,
          variant: firstAvailableVariant(product),
          categoryId: def.id,
          categoryLabel: label,
          requires_date: !!def.requires_date,
          requires_time_window: !!def.requires_time_window,
          requires_vet_clinic: !!def.requires_vet_clinic,
          requires_address_choice: !!def.requires_address_choice,
          sms_note: '',
          source: 'category-base',
        }]
      }
      return {
        id: def.id,
        label: label,
        product: product,
        options: options,
        tileDesc: def.id === 'lonsdale' ? lonsdaleHoursHelperText(config) : (def.tileDesc || ''),
        requires_date: !!def.requires_date,
        requires_time_window: !!def.requires_time_window,
        requires_vet_clinic: !!def.requires_vet_clinic,
        requires_address_choice: !!def.requires_address_choice,
        serviceHeadline: def.serviceHeadline,
        sms_note: resolveTransportSmsNote(def, notes),
      }
    }).filter(Boolean)
  }

  function getTransportCategories(config) {
    return buildTransportCategories(config)
  }

  function findTransportCategory(config, categoryId) {
    return getTransportCategories(config).find(function (c) { return c.id === categoryId }) || null
  }

  function optionsForTransportCategory(config, categoryId) {
    var cat = findTransportCategory(config, categoryId)
    return cat ? cat.options.slice() : []
  }

  function getTransportMainOptions(config) {
    var all = []
    getTransportCategories(config).forEach(function (c) {
      all = all.concat(c.options)
    })
    return all
  }

  function needsTransportCategoryStep(config) {
    return getTransportCategories(config).length > 0
  }

  function categoryNeedsServiceStep(category) {
    if (!category) return false
    return category.options.length > 1
  }

  function categoryTilePriceLabel(category, moneyFn) {
    var prices = (category.options || [])
      .map(function (o) { return o.variant ? Number(o.variant.price) || 0 : 0 })
    if (!prices.length) return ''
    var min = Math.min.apply(null, prices)
    var max = Math.max.apply(null, prices)
    // Single clear price (e.g. Home $200) → show it. All free → Included.
    // Mixed free + paid (Lonsdale / Vet upgrades) → leave blank (avoid misleading “From $max”).
    if (min === 0 && max === 0) return 'Included'
    if (min === max) return moneyFn(min)
    return ''
  }

  function findTransportMainOption(config, key) {
    return getTransportMainOptions(config).find(function (o) { return o.key === key }) || null
  }

  function requirementsForTransportState(state, config) {
    var cat = findTransportCategory(config, state.transport.categoryId)
    if (!cat) {
      var opt = findTransportMainOption(config, state.transport.key)
      return opt || {}
    }
    var req = {
      requires_date: !!cat.requires_date,
      requires_time_window: !!cat.requires_time_window,
      requires_vet_clinic: !!cat.requires_vet_clinic,
      requires_address_choice: !!cat.requires_address_choice,
      sms_note: cat.sms_note || '',
      detailsHeadline: 'A few pickup details',
      dateLabel: 'Preferred date',
      timeWindowLabel: 'Preferred time window',
    }
    var def = transportCategoryDef(cat.id)
    if (def) {
      req.detailsHeadline = def.detailsHeadline || 'A few pickup details'
      req.dateLabel = def.dateLabel || 'Preferred date'
      req.timeWindowLabel = def.timeWindowLabel || 'Preferred time window'
    }
    if (cat.id === 'lonsdale') {
      req.detailsHeadline = stepCopyText(config, 'transportLonsdaleDetailsHeadline')
    } else if (cat.id === 'vet') {
      req.detailsHeadline = stepCopyText(config, 'transportVetDetailsHeadline')
    } else if (cat.id === 'home') {
      req.detailsHeadline = stepCopyText(config, 'transportHomeDetailsHeadline')
    }
    return req
  }

  function transportLeaf(state, config) {
    if (!state.transport.key && !state.transport.categoryId) return null
    return requirementsForTransportState(state, config)
  }

  function centsPriceLabel(variant, moneyFn) {
    if (!variant) return 'Included'
    var cents = Number(variant.price) || 0
    return cents ? moneyFn(cents) : 'Included'
  }

  // Australia-only storefront — drop trailing country from Places / typed addresses.

  function formatPickupAddress(address) {
    return String(address || '')
      .replace(/\s*,\s*Australia\s*$/i, '')
      .replace(/\s*,\s*AU\s*$/i, '')
      .replace(/\s+/g, ' ')
      .trim()
  }

  /**
   * Sidebar Booking Summary only — compact street + suburb.
   * e.g. "22 Ifould Street, Adelaide SA 5000" → "22 Ifould St, Adelaide"
   * Facility labels like "Angel Ashes Lonsdale" stay as-is.
   * Cart / confirm cards / Review keep formatPickupAddress.
   */

  function formatSummaryAddress(address) {
    var cleaned = formatPickupAddress(address)
    if (!cleaned) return ''
    // Named facility / centre lines — do not street-abbreviate.
    if (/^Angel Ashes\b/i.test(cleaned) && cleaned.indexOf(',') === -1) return cleaned

    // Drop trailing Australian state + postcode (keep suburb).
    cleaned = cleaned.replace(/,?\s*\b(?:NSW|VIC|QLD|SA|WA|TAS|ACT|NT)\s+\d{4}\s*$/i, '')
    cleaned = cleaned.replace(/,?\s*\b\d{4}\s*$/, '')

    var comma = cleaned.indexOf(',')
    var street = (comma >= 0 ? cleaned.slice(0, comma) : cleaned).trim()
    var locality = comma >= 0 ? cleaned.slice(comma + 1).trim() : ''

    street = street
      .replace(/\bStreet\b/gi, 'St')
      .replace(/\bRoad\b/gi, 'Rd')
      .replace(/\bAvenue\b/gi, 'Ave')
      .replace(/\bDrive\b/gi, 'Dr')
      .replace(/\bCourt\b/gi, 'Ct')
      .replace(/\bTerrace\b/gi, 'Tce')
      .replace(/\bBoulevard\b/gi, 'Bvd')
      .replace(/\bBlvd\b/gi, 'Bvd')

    if (street && locality) return street + ', ' + locality
    return street || locality || cleaned
  }

  /**
   * Confirm-card address: street on its own line, locality below when comma-separated.
   * Full text wraps — no ellipsis clip (sidebar uses formatSummaryAddress).
   */

  function formatAddressConfirmHtml(address) {
    var cleaned = formatPickupAddress(address)
    if (!cleaned) return ''
    var comma = cleaned.indexOf(',')
    if (comma > 0) {
      var street = cleaned.slice(0, comma).trim()
      var locality = cleaned.slice(comma + 1).trim()
      if (street && locality) {
        return (
          '<span class="baw-address-confirm-line">' + escapeHtml(street) + '</span>' +
          '<span class="baw-address-confirm-line">' + escapeHtml(locality) + '</span>'
        )
      }
    }
    return '<span class="baw-address-confirm-line">' + escapeHtml(cleaned) + '</span>'
  }

  /**
   * Home Collection needs a street number and/or unit — not a street name alone
   * (e.g. reject “The Parade, Norwood SA 5067”).
   * Prefer Places address_components; fall back to the formatted street line.
   */

  function streetLineHasNumberOrUnit(streetLine) {
    var line = String(streetLine || '').trim()
    if (!line) return false
    // 44 Balee Ave · 10A Smith St · 2/10 Smith St
    if (/^\d+[A-Za-z]?(?:\s*[-/]\s*\d+[A-Za-z]?)?\b/.test(line)) return true
    // Unit 2/10 Smith St · Shop 1 · Apt 3
    if (/\b(?:unit|apt|apartment|suite|level|shop|lot|u)\s*\d+/i.test(line)) return true
    return false
  }

  function formattedAddressHasStreetNumberOrUnit(address) {
    var cleaned = formatPickupAddress(address)
    if (!cleaned) return false
    var comma = cleaned.indexOf(',')
    var streetLine = comma > 0 ? cleaned.slice(0, comma).trim() : cleaned
    return streetLineHasNumberOrUnit(streetLine)
  }

  /** null = components unavailable; true/false when Google returned them. */

  function addressComponentsHaveStreetNumberOrUnit(components) {
    if (!Array.isArray(components) || !components.length) return null
    var hasStreetNumber = false
    var hasSubpremise = false
    for (var i = 0; i < components.length; i++) {
      var types = (components[i] && components[i].types) || []
      for (var t = 0; t < types.length; t++) {
        if (types[t] === 'street_number') hasStreetNumber = true
        if (types[t] === 'subpremise') hasSubpremise = true
      }
    }
    return hasStreetNumber || hasSubpremise
  }

  function placeHasStreetNumberOrUnit(place) {
    if (!place) return false
    var fromComponents = addressComponentsHaveStreetNumberOrUnit(place.address_components)
    if (fromComponents !== null) return fromComponents
    return formattedAddressHasStreetNumberOrUnit(place.formatted_address || '')
  }

  function isHomeAddressInvalid(transport) {
    return !!(transport && (transport.addressOutOfArea || transport.addressMissingStreetNumber))
  }

  /** True when Places search should hide — confirm card only (Change expands). */

  function isHomeAddressCollapsed(state, config) {
    if (!state || !state.transport || state.transport.addressEditing) return false
    if (!String(state.transport.newAddress || '').trim()) return false
    if (
      hasHomeServiceArea(config) &&
      (state.transport.newAddressLat == null || state.transport.newAddressLng == null)
    ) {
      return false
    }
    return true
  }

  /**
   * Theme Editor polygon for Home Collection.
   * Accepts JSON `[{lat,lng},…]` or newline `lat,lng` pairs. Empty → allow-all.
   */

  function parseServiceAreaPolygon(raw) {
    var text = String(raw == null ? '' : raw).trim()
    if (!text) return []
    var points = []
    if (text.charAt(0) === '[') {
      try {
        var parsed = JSON.parse(text)
        if (Array.isArray(parsed)) {
          parsed.forEach(function (p) {
            if (!p || typeof p !== 'object') return
            var lat = Number(p.lat != null ? p.lat : p.latitude)
            var lng = Number(p.lng != null ? p.lng : (p.lon != null ? p.lon : p.longitude))
            if (isFinite(lat) && isFinite(lng)) points.push({ lat: lat, lng: lng })
          })
        }
      } catch (err) {
        points = []
      }
    }
    if (!points.length) {
      text.split(/\r?\n/).forEach(function (line) {
        var s = String(line || '').trim()
        if (!s || s.charAt(0) === '#') return
        var m = s.match(/^(-?\d+(?:\.\d+)?)\s*[, ]\s*(-?\d+(?:\.\d+)?)\s*$/)
        if (!m) return
        var lat = Number(m[1])
        var lng = Number(m[2])
        if (isFinite(lat) && isFinite(lng)) points.push({ lat: lat, lng: lng })
      })
    }
    if (points.length >= 2) {
      var first = points[0]
      var last = points[points.length - 1]
      if (first.lat === last.lat && first.lng === last.lng) points = points.slice(0, -1)
    }
    return points.length >= 3 ? points : []
  }

  function hasHomeServiceArea(config) {
    return !!(config && Array.isArray(config.homeCollectionServiceArea) && config.homeCollectionServiceArea.length >= 3)
  }

  /** Ray-casting point-in-polygon (lat/lng as y/x). Empty polygon → allow-all. */

  function pointInPolygon(lat, lng, polygon) {
    if (!isFinite(lat) || !isFinite(lng) || !polygon || polygon.length < 3) return true
    var inside = false
    for (var i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      var yi = polygon[i].lat
      var xi = polygon[i].lng
      var yj = polygon[j].lat
      var xj = polygon[j].lng
      if (yj === yi) continue
      if (yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) {
        inside = !inside
      }
    }
    return inside
  }

  function isHomeAddressInServiceArea(lat, lng, config) {
    if (!hasHomeServiceArea(config)) return true
    if (lat == null || lng == null || !isFinite(lat) || !isFinite(lng)) return false
    return pointInPolygon(lat, lng, config.homeCollectionServiceArea)
  }

  var HOME_OUT_OF_AREA_MESSAGE =
    "Home Collection isn't available for this address. Please choose Bring to Lonsdale or Vet Collection instead."

  var HOME_MISSING_STREET_NUMBER_MESSAGE =
    'Please choose an address that includes a street number.'

  function clearPickupAddressState(transport) {
    transport.newAddress = ''
    transport.newAddressLat = null
    transport.newAddressLng = null
    transport.addressOutOfArea = false
    transport.addressMissingStreetNumber = false
    transport.addressEditing = false
  }

  function isPickupAddressReady(state, config) {
    if (!String(state.transport.newAddress || '').trim()) return false
    if (isHomeAddressInvalid(state.transport)) return false
    if (hasHomeServiceArea(config)) {
      // Polygon set: require a Places pick with coords (Change reopens search → not ready yet)
      if (state.transport.addressEditing) return false
      return state.transport.newAddressLat != null && state.transport.newAddressLng != null
    }
    // No polygon: non-empty address with a street/unit number (typed or Places)
    return !state.transport.addressEditing
  }

  function pad2(n) {
    return (n < 10 ? '0' : '') + n
  }

  function toYmd(date) {
    return date.getFullYear() + '-' + pad2(date.getMonth() + 1) + '-' + pad2(date.getDate())
  }

  function parseYmdLocal(ymd) {
    var m = String(ymd || '').match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (!m) return null
    var d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    return isNaN(d.getTime()) ? null : d
  }

  function addDaysLocal(date, days) {
    var d = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    d.setDate(d.getDate() + days)
    return d
  }

  /** Advance by N Mon–Fri working days (Sat/Sun skipped). */

  function addWorkingDaysLocal(date, days) {
    var d = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    var left = days
    while (left > 0) {
      d = addDaysLocal(d, 1)
      var dow = d.getDay()
      if (dow !== 0 && dow !== 6) left -= 1
    }
    return d
  }

  function parseFlexibleDateLocal(raw) {
    var s = String(raw || '').trim()
    var iso = s.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (iso) return parseYmdLocal(iso[0])
    var au = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/)
    if (au) {
      var day = Number(au[1])
      var month = Number(au[2])
      var year = Number(au[3])
      if (month < 1 || month > 12 || day < 1 || day > 31) return null
      var d = new Date(year, month - 1, day)
      if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null
      return d
    }
    return null
  }

  function parseClosedDateLines(lines) {
    var closed = {}
    ;(lines || []).forEach(function (line) {
      var raw = String(line || '').trim()
      if (!raw || raw.charAt(0) === '#') return
      var range = raw.match(/^(.+?)\s+(?:to|–|—)\s+(.+)$/i)
      if (range) {
        var start = parseFlexibleDateLocal(range[1])
        var end = parseFlexibleDateLocal(range[2])
        if (!start || !end) return
        if (end < start) {
          var tmp = start
          start = end
          end = tmp
        }
        for (var cur = start; cur <= end; cur = addDaysLocal(cur, 1)) {
          closed[toYmd(cur)] = true
        }
        return
      }
      var single = parseFlexibleDateLocal(raw)
      if (single) closed[toYmd(single)] = true
    })
    return closed
  }

  function isAfterHoursTransport(state) {
    var blob = [
      state.transport.label,
      state.transport.subLabel,
      state.transport.categoryLabel,
    ].filter(Boolean).join(' ')
    return isAfterHoursOptionLabel(blob)
  }

  /**
   * After Hours is home-only. If state still has Vet + After Hours (e.g. back
   * navigation from a prior selection), or any vet service key that is no
   * longer in the filtered options, clear the service level so Continue cannot
   * advance with an invalid combo. Keeps the Vet path selected.
   */

  function reconcileVetAfterHoursSelection(state, config) {
    if (!state || !state.transport) return false
    if (!isVetTransport(state)) return false

    var invalid =
      isAfterHoursTransport(state) ||
      (state.transport.key &&
        !(optionsForTransportCategory(config, 'vet') || []).some(function (o) {
          return o.key === state.transport.key
        }))
    if (!invalid) return false

    state.transport.key = null
    state.transport.label = null
    state.transport.variant = null
    state.transport.subKey = null
    state.transport.subLabel = null
    state.transport.subVariant = null
    state.transport.date = null
    state.transport.calendarMonth = null
    state.transport.timeWindow = null
    clearVetClinicState(state.transport)
    clearPickupAddressState(state.transport)

    var stepId = String(state.currentStepId || '')
    if (
      stepId === 'transport-sub' ||
      stepId === 'transport-details' ||
      stepId.indexOf('memorial:') === 0 ||
      stepId === 'return-speed' ||
      stepId === 'return-method' ||
      stepId === 'review'
    ) {
      state.currentStepId = 'transport-main'
    }
    return true
  }

  /** Home Standard / Regional (not After Hours) — weekday collection calendar. */

  function isHomeNonAfterHoursTransport(state) {
    return !!(
      state &&
      state.transport &&
      state.transport.categoryId === 'home' &&
      !isAfterHoursTransport(state)
    )
  }

  /** Ops scheduling TZ — same-day cutoff is Adelaide wall-clock, not browser local. */
  var BOOKING_OPS_TIMEZONE = 'Australia/Adelaide'

  /** Theme Editor hour (0–23); invalid / blank values fall back to `fallback` (default 13). */

  function parseSameDayCutoffHour(raw, fallback) {
    var def = typeof fallback === 'number' ? fallback : 13
    if (raw === null || raw === undefined || raw === '') return def
    var n = Number(raw)
    if (!isFinite(n)) return def
    return Math.max(0, Math.min(23, Math.floor(n)))
  }

  function adelaideDateTimeParts(now) {
    var parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: BOOKING_OPS_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(now || new Date())
    var map = {}
    for (var i = 0; i < parts.length; i++) {
      if (parts[i].type !== 'literal') map[parts[i].type] = parts[i].value
    }
    return {
      year: Number(map.year),
      month: Number(map.month),
      day: Number(map.day),
      hour: Number(map.hour),
      minute: Number(map.minute),
      ymd: map.year + '-' + map.month + '-' + map.day,
    }
  }

  /**
   * Parse a preferred-time label ("10:00am", "12:45pm") to minutes since midnight.
   * Matches ops import parsing; returns null when the label is not a clock time.
   */
  function parsePreferredTimeLabelMinutes(label) {
    var s = String(label || '').trim()
    if (!s) return null
    var clock = s.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)\b/i)
    if (clock) {
      var hour = Number(clock[1])
      var minute = clock[2] ? Number(clock[2]) : 0
      var meridiem = clock[3].toLowerCase()
      if (!isFinite(hour) || !isFinite(minute) || minute < 0 || minute > 59) return null
      if (meridiem === 'pm' && hour < 12) hour += 12
      if (meridiem === 'am' && hour === 12) hour = 0
      if (hour < 0 || hour > 23) return null
      return hour * 60 + minute
    }
    var h24 = s.match(/\b([01]?\d|2[0-3]):([0-5]\d)\b/)
    if (h24) {
      return Number(h24[1]) * 60 + Number(h24[2])
    }
    return null
  }

  /**
   * For Adelaide "today", hide slots at or before the current Adelaide minute.
   * Future Adelaide days keep the full list. No extra lead-time buffer — product
   * same-day rules live on the calendar cutoff hour, not per-slot padding.
   */
  function filterArrivalTimeWindowsForDate(timeOptions, selectedYmd, now) {
    var options = Array.isArray(timeOptions) ? timeOptions : []
    if (!selectedYmd || !options.length) return options.slice()
    var parts = adelaideDateTimeParts(now)
    if (selectedYmd !== parts.ymd) return options.slice()
    var nowMinutes = parts.hour * 60 + parts.minute
    return options.filter(function (label) {
      var slotMinutes = parsePreferredTimeLabelMinutes(label)
      // Unparseable labels stay visible (config / window-style strings).
      if (slotMinutes == null) return true
      return slotMinutes > nowMinutes
    })
  }

  /** Configured arrival slots minus past Adelaide times when the selected day is today. */
  function bookableArrivalTimeWindowsForState(state, config, now) {
    var options = timeWindowOptionsForState(state, config) || []
    var ymd = state && state.transport ? state.transport.date : null
    return filterArrivalTimeWindowsForDate(options, ymd, now)
  }

  /** Clear a stale selected slot that is no longer bookable (e.g. page left open). */
  function reconcileArrivalTimeWindowSelection(state, config, now) {
    if (!state || !state.transport || !state.transport.timeWindow) return false
    var bookable = bookableArrivalTimeWindowsForState(state, config, now)
    if (bookable.indexOf(state.transport.timeWindow) !== -1) return false
    state.transport.timeWindow = null
    return true
  }

  /** True when Adelaide today — used for empty-state copy after slot filtering. */
  function isAdelaideTodayYmd(ymd, now) {
    if (!ymd) return false
    return ymd === adelaideDateTimeParts(now).ymd
  }

  /** True when Adelaide local time is at/after the given cutoff hour (0–23). */

  function isPastSameDayCutoff(cutoffHour, now) {
    if (typeof cutoffHour !== 'number') return false
    return adelaideDateTimeParts(now).hour >= cutoffHour
  }

  /**
   * Adelaide "today" is not bookable once past the path cutoff hour.
   * Cutoff hour comes from Theme Editor (home / Lonsdale / vet).
   */

  function isSameDayBlockedByCutoff(ymd, cutoffHour, now) {
    if (!ymd || typeof cutoffHour !== 'number') return false
    var parts = adelaideDateTimeParts(now)
    if (ymd !== parts.ymd) return false
    return parts.hour >= cutoffHour
  }

  /**
   * Per-path Adelaide same-day cutoff hour, or null when cutoffs are off / path exempt.
   * Theme toggle off → null (calendars behave as before). Vet + After Hours home never get a cutoff.
   */

  function sameDayCutoffHourForState(config, state) {
    if (!state || !state.transport) return null
    if (isAfterHoursTransport(state)) return null
    if (isVetTransport(state)) return null
    var cal = (config && config.bookingCalendar) || {}
    if (cal.sameDayCutoffEnabled === false) return null
    if (isHomeNonAfterHoursTransport(state)) {
      return typeof cal.sameDayCutoffHome === 'number' ? cal.sameDayCutoffHome : 13
    }
    if (isLonsdaleTransport(state)) {
      return typeof cal.sameDayCutoffLonsdale === 'number' ? cal.sameDayCutoffLonsdale : 13
    }
    return null
  }

  /** Calm display for Theme Editor hour 0–23 — e.g. 11 → "11am", 13 → "1pm". */

  function formatSameDayCutoffHourLabel(hour) {
    if (typeof hour !== 'number' || !isFinite(hour)) return ''
    var h = Math.max(0, Math.min(23, Math.floor(hour)))
    var suffix = h < 12 ? 'am' : 'pm'
    var twelve = h % 12
    if (twelve === 0) twelve = 12
    return String(twelve) + suffix
  }

  /**
   * Quiet one-liner when Adelaide "today" is blocked by this path's same-day cutoff.
   * Empty for Vet, After Hours, before cutoff, weekends (weekday paths), and closed dates.
   */

  function sameDayCutoffHintForState(config, state) {
    // Vet: no same-day cutoff (today always selectable subject to weekends/closed/lead days).
    if (isVetTransport(state)) return ''
    var cutoffHour = sameDayCutoffHourForState(config, state)
    if (typeof cutoffHour !== 'number') return ''
    if (!isPastSameDayCutoff(cutoffHour)) return ''
    var parts = adelaideDateTimeParts()
    var rules = bookingDateRules(config, state)
    if (rules.closedDates && rules.closedDates[parts.ymd]) return ''
    var d = parseYmdLocal(parts.ymd)
    if (!d) return ''
    var day = d.getDay()
    if (!rules.allowWeekends && (day === 0 || day === 6)) return ''
    var hourLabel = formatSameDayCutoffHourLabel(cutoffHour)
    if (!hourLabel) return ''
    if (isLonsdaleTransport(state)) {
      return 'Same-day visits aren\u2019t available after ' + hourLabel + '.'
    }
    // Home Standard/Regional
    return 'Same-day collection isn\u2019t available after ' + hourLabel + '.'
  }

  /** True when the current path’s service-level options include an After Hours variant. */

  function categoryOffersAfterHours(config, state) {
    if (!state || !state.transport) return false
    var cat = findTransportCategory(config, state.transport.categoryId)
    var options = cat ? cat.options : getTransportMainOptions(config)
    return (options || []).some(function (o) {
      return /after\s*-?\s*hours?/i.test((o && o.label) || '')
    })
  }

  /**
   * Heading subtext when same-day is blocked — not under the calendar (keeps cal + side card aligned).
   * Home: primary cutoff line + quieter Same-Day Collection CTA on the next line (transport-main).
   * Lonsdale: preferred-visit copy only (single quiet line). Vet: never.
   */

  function sameDayCutoffHintHtml(config, state) {
    var hint = sameDayCutoffHintForState(config, state)
    if (!hint) return ''
    var body =
      '<span class="baw-helper-line">' + escapeHtml(hint) + '</span>'
    // Home only — secondary line to Same-Day when that option exists (same goto as Priority).
    if (isHomeNonAfterHoursTransport(state) && categoryOffersAfterHours(config, state)) {
      body +=
        '<span class="baw-helper-line baw-helper-line--secondary">' +
        escapeHtml('Need service today? Choose ') +
        '<button type="button" class="baw-inline-link" data-baw-goto="transport-main" data-baw-preselect-service="same-day">After Hours and Public Holiday Collection</button>' +
        escapeHtml('.') +
        '</span>'
    }
    return (
      '<div class="baw-helper baw-helper--quiet" data-baw-same-day-cutoff-hint>' +
      body +
      '</div>'
    )
  }

  function bookingDateRules(config, state) {
    var cal = config.bookingCalendar || {}
    var allowWeekends = isAfterHoursTransport(state)
    var cutoffHour = sameDayCutoffHourForState(config, state)
    var appliesCutoff = typeof cutoffHour === 'number'
    var minLeadDays = typeof cal.minLeadDays === 'number' ? cal.minLeadDays : 1
    // Paths with a cutoff: same-day is bookable before the Adelaide hour
    // (weekday / closed-date rules still apply). After cutoff, require tomorrow+.
    if (appliesCutoff) {
      minLeadDays = isPastSameDayCutoff(cutoffHour) ? Math.max(minLeadDays, 1) : 0
    }
    return {
      allowWeekends: allowWeekends,
      closedDates: cal.closedDates || {},
      minLeadDays: minLeadDays,
      horizonDays: typeof cal.horizonDays === 'number' ? cal.horizonDays : 90,
      sameDayCutoffHour: appliesCutoff ? cutoffHour : null,
    }
  }

  function isBookingDateAllowed(ymd, rules) {
    if (!ymd || !parseYmdLocal(ymd)) return false
    if (rules.closedDates[ymd]) return false
    var d = parseYmdLocal(ymd)
    var day = d.getDay()
    if (!rules.allowWeekends && (day === 0 || day === 6)) return false
    if (
      typeof rules.sameDayCutoffHour === 'number' &&
      isSameDayBlockedByCutoff(ymd, rules.sameDayCutoffHour)
    ) {
      return false
    }
    return true
  }

  function earliestBookingDate(rules) {
    var lead = typeof rules.minLeadDays === 'number' ? rules.minLeadDays : 0
    var base = new Date()
    // Anchor cutoff paths to Adelaide calendar day so overseas browsers match
    // clinic scheduling for the same-day rule.
    if (typeof rules.sameDayCutoffHour === 'number') {
      var parts = adelaideDateTimeParts()
      base = new Date(parts.year, parts.month - 1, parts.day)
    }
    return addDaysLocal(base, lead)
  }

  function buildBookableDates(rules) {
    var out = []
    var start = earliestBookingDate(rules)
    var horizon = rules.horizonDays || 90
    for (var i = 0; i <= horizon; i++) {
      var d = addDaysLocal(start, i)
      var ymd = toYmd(d)
      if (isBookingDateAllowed(ymd, rules)) out.push(ymd)
    }
    return out
  }

  /** Listed dates for families, cart, and checkout — Australian day-month-year. */
  function formatAuDateDmy(ymd) {
    var d = parseYmdLocal(ymd)
    if (!d) return String(ymd || '')
    return pad2(d.getDate()) + '-' + pad2(d.getMonth() + 1) + '-' + d.getFullYear()
  }

  function formatBookableDateLabel(ymd) {
    var d = parseYmdLocal(ymd)
    if (!d) return ymd
    var days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    return days[d.getDay()] + ' ' + d.getDate() + ' ' + months[d.getMonth()] + ' ' + d.getFullYear()
  }

  /** Compact date for collection ranges — e.g. Thu 6 Aug (no year). */

  function formatCollectionDateShort(ymd) {
    var d = parseYmdLocal(ymd)
    if (!d) return String(ymd || '')
    var days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    return days[d.getDay()] + ' ' + d.getDate() + ' ' + months[d.getMonth()]
  }

  function formatCollectionDateRange(startYmd, endYmd) {
    if (!startYmd || !endYmd) return ''
    return formatCollectionDateShort(startYmd) + ' \u2013 ' + formatCollectionDateShort(endYmd)
  }

  /**
   * Sidebar Booking Summary only — single date without year.
   * e.g. "Fri 7 Aug 2026" → "Fri 7 Aug" (pair with time as "Fri 7 Aug · 10:00am").
   */

  function formatSummaryDateLabel(ymd) {
    return formatCollectionDateShort(ymd)
  }

  /**
   * Sidebar Booking Summary only — denser range than formatCollectionDateRange.
   * Same month: "Mon 17 Aug – Fri 21 Aug" → "17–21 Aug"
   * Cross-month: "31 Aug – 2 Sep"
   */

  function formatSummaryDateRange(startYmd, endYmd) {
    if (!startYmd || !endYmd) return ''
    var start = parseYmdLocal(startYmd)
    var end = parseYmdLocal(endYmd)
    if (!start || !end) return formatCollectionDateRange(startYmd, endYmd)
    var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    var sm = months[start.getMonth()]
    var em = months[end.getMonth()]
    if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
      return start.getDate() + '\u2013' + end.getDate() + ' ' + sm
    }
    return start.getDate() + ' ' + sm + ' \u2013 ' + end.getDate() + ' ' + em
  }

  function petFirstNameRef(state) {
    var petName =
      state && state.pet && state.pet.name
        ? String(state.pet.name).trim().split(/\s+/)[0]
        : ''
    return { name: petName, ref: petName || 'your pet' }
  }

  /**
   * Numeric collection window from the ready-from date (vet only).
   * Standard / Regional: +7 calendar days (tile “Up to 7 days”; e.g. Thu 6 Aug – Thu 13 Aug).
   * Priority: +2 working days (weekends skipped).
   * Home / After Hours / Lonsdale: null (home is a dedicated requested date).
   */

  function collectionEstimateRangeForState(state) {
    if (!state || !state.transport || !state.transport.date) return null
    if (isLonsdaleTransport(state)) return null
    if (isAfterHoursTransport(state)) return null
    // Home is a dedicated requested date — ranges are vet-only.
    if (!isVetTransport(state)) return null

    var start = parseYmdLocal(state.transport.date)
    if (!start) return null

    var isPriority = isPriorityCollectionSelected(state)
    var end = isPriority ? addWorkingDaysLocal(start, 2) : addDaysLocal(start, 7)
    var endYmd = toYmd(end)
    return {
      startYmd: state.transport.date,
      endYmd: endYmd,
      rangeLabel: formatCollectionDateRange(state.transport.date, endYmd),
      kind: isPriority ? 'priority' : 'standard',
    }
  }

  /** Intake confirmation — distinct from ashes-return “ready to come home”. */

  function collectionInCareEmailHelper(state, config) {
    if (!state || !state.transport) return ''
    if (isLonsdaleTransport(state)) return ''
    var isHome = state.transport.categoryId === 'home'
    var isVet = isVetTransport(state)
    if (!isVet && !isHome) return ''
    var template = stepCopyText(config, 'transportVetDetailsSubtitle')
    var pet = petFirstNameRef(state)
    var possess = pet.name ? possessiveName(pet.name) : 'the'
    return String(template || '')
      .replace(/\{pet_possessive\}/g, possess)
      .replace(/\{pet\}/g, pet.ref)
  }

  /** Vet ready-from date step — calm subheading under the page title (not GuidanceBar). */

  function collectionInCareEmailSubheadingHtml(state, config) {
    if (!state || !state.transport || !isVetTransport(state)) return ''
    var text = collectionInCareEmailHelper(state, config)
    if (!text) return ''
    return '<p class="baw-helper">' + escapeHtml(text) + '</p>'
  }

  function buildPickupAddressConfirmHtml(state, config) {
    if (!isHomeAddressCollapsed(state, config)) return ''
    var invalid = isHomeAddressInvalid(state.transport)
    var addressHtml = formatAddressConfirmHtml(state.transport.newAddress)
    if (!addressHtml) return ''
    // Outer field label already says “Collection address *” — card is check + address + Change.
    return (
      '<div class="baw-address-confirm" role="status">' +
      '<div class="baw-address-confirm-top">' +
      '<span class="baw-address-confirm-check' + (invalid ? ' is-error' : '') + '" aria-hidden="true">' +
      (invalid ? '✕' : '✓') +
      '</span>' +
      '<div class="baw-address-confirm-main">' +
      '<p class="baw-address-confirm-text">' + addressHtml + '</p>' +
      '</div>' +
      '<button type="button" class="baw-address-change" data-baw-address-change>Change</button>' +
      '</div>' +
      '</div>'
    )
  }

  function buildPickupAddressFieldHtml(state, config, smsNote) {
    var collapsed = isHomeAddressCollapsed(state, config)
    // Confirmed address: collapse Places search (Change expands again).
    var showSearch = !collapsed
    var confirmHtml = buildPickupAddressConfirmHtml(state, config)
    var addressInvalid = isHomeAddressInvalid(state.transport)
    var errorHtml = ''
    if (state.transport.addressMissingStreetNumber) {
      errorHtml =
        '<p class="baw-address-error" role="alert">' +
        escapeHtml(HOME_MISSING_STREET_NUMBER_MESSAGE) +
        '</p>'
    } else if (state.transport.addressOutOfArea) {
      errorHtml =
        '<p class="baw-address-error" role="alert">' + escapeHtml(HOME_OUT_OF_AREA_MESSAGE) + '</p>'
    }
    // Home SMS note sits under the address field (not the calendar) — arrangement context.
    // Hide while the selected address fails street-number or service-area checks.
    var noteHtml =
      smsNote && !addressInvalid
        ? '<div class="baw-note">' + escapeHtml(smsNote) + '</div>'
        : ''
    return (
      '<div class="baw-field baw-pickup-side baw-pickup-address' + (collapsed ? ' is-confirmed' : '') + '" role="group" aria-labelledby="baw-t-address-label">' +
      '<label class="baw-label" id="baw-t-address-label">Collection address <span class="baw-required">*</span></label>' +
      '<div class="baw-pickup-side-body">' +
      (showSearch
        ? '<input class="baw-input" id="baw-t-address" type="text" placeholder="Start typing your address" autocomplete="off" aria-labelledby="baw-t-address-label" value="' + escapeHtml(state.transport.newAddress || '') + '" />'
        : '') +
      confirmHtml +
      errorHtml +
      noteHtml +
      '</div>' +
      '</div>'
    )
  }

  /**
   * Angel Ashes partner clinics — quick-picks above Places search.
   * Metro pins show for Adelaide Vet Collection; regional for South East.
   */
  var PARTNER_VET_CLINICS = [
    {
      id: 'pet-universe-broadview',
      name: 'Pet Universe · Broadview',
      address: '71 Hampstead Road, Manningham SA 5086',
      region: 'metro',
    },
    {
      id: 'pet-universe-northgate',
      name: 'Pet Universe · Northgate',
      address: '2-10 City View Boulevard, Northgate SA 5085',
      region: 'metro',
    },
    {
      id: 'north-east-vet',
      name: 'North East Vet · Dernancourt',
      address: 'Shop 1/850 Lower North East Road, Dernancourt SA 5075',
      region: 'metro',
    },
    {
      id: 'south-east-vets-mount-gambier',
      name: 'South East Vets · Mount Gambier',
      address: '314 Commercial Street West, Mount Gambier SA 5290',
      region: 'regional',
    },
    {
      id: 'millicent-vet',
      name: 'Millicent Veterinary Clinic · Millicent',
      address: '62 Mount Gambier Road, Millicent SA 5280',
      region: 'regional',
    },
  ]

  /**
   * Clinics Google Places will not list (mobile / unlisted).
   * Hidden from the quick-picks — they appear only when searched.
   */
  var SEARCHABLE_VET_CLINICS = [
    {
      id: 'horizon-palliative-vets',
      name: 'Horizon Palliative Vets',
      address: '10 Sixth Avenue, Warradale SA 5046',
      region: 'metro',
    },
  ]

  var VET_OUT_OF_AREA_MESSAGE =
    "This clinic is outside our collection area. Please choose a clinic closer to Adelaide, or select Regional Collection for South East clinics."

  function partnerVetClinicsForState(state) {
    var regional = isRegionalCareArrangement(state)
    return PARTNER_VET_CLINICS.filter(function (clinic) {
      return regional ? clinic.region === 'regional' : clinic.region === 'metro'
    })
  }

  function searchableVetClinicsForState(state) {
    var regional = isRegionalCareArrangement(state)
    return SEARCHABLE_VET_CLINICS.filter(function (clinic) {
      return regional ? clinic.region === 'regional' : clinic.region === 'metro'
    })
  }

  function matchingSearchableVetClinics(state, query) {
    var tokens = String(query || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .split(/\s+/)
      .filter(Boolean)
    if (!tokens.length) return []
    return searchableVetClinicsForState(state).filter(function (clinic) {
      var hay = (clinic.name + ' ' + clinic.address).toLowerCase()
      return tokens.every(function (token) {
        return hay.indexOf(token) !== -1
      })
    })
  }

  function partnerVetClinicById(id) {
    var key = String(id || '')
    var lists = [PARTNER_VET_CLINICS, SEARCHABLE_VET_CLINICS]
    for (var n = 0; n < lists.length; n++) {
      for (var i = 0; i < lists[n].length; i++) {
        if (lists[n][i].id === key) return lists[n][i]
      }
    }
    return null
  }

  function clearVetClinicState(transport) {
    transport.vetClinic = null
    transport.vetAddress = null
    transport.vetClinicId = null
    transport.vetClinicEditing = false
    transport.vetClinicOutOfArea = false
  }

  function isVetClinicReady(state) {
    if (!String(state.transport.vetClinic || '').trim()) return false
    if (!String(state.transport.vetAddress || '').trim()) return false
    if (state.transport.vetClinicEditing) return false
    if (state.transport.vetClinicOutOfArea) return false
    return true
  }

  /** Metro Places picks use the Home Collection polygon when configured. */

  function isVetClinicInServiceArea(lat, lng, state, config) {
    if (isRegionalCareArrangement(state)) return true
    if (!hasHomeServiceArea(config)) return true
    if (lat == null || lng == null || !isFinite(lat) || !isFinite(lng)) return false
    return pointInPolygon(lat, lng, config.homeCollectionServiceArea)
  }

  function buildVetClinicConfirmHtml(state) {
    var name = String(state.transport.vetClinic || '').trim()
    var address = String(state.transport.vetAddress || '').trim()
    if (!name || !address || state.transport.vetClinicEditing) return ''
    var outOfArea = !!state.transport.vetClinicOutOfArea
    return (
      '<div class="baw-address-confirm baw-vet-confirm" role="status">' +
      '<div class="baw-address-confirm-top">' +
      '<span class="baw-address-confirm-check' + (outOfArea ? ' is-error' : '') + '" aria-hidden="true">' +
      (outOfArea ? '✕' : '✓') +
      '</span>' +
      '<span class="baw-address-confirm-label">Vet clinic</span>' +
      '<button type="button" class="baw-address-change" data-baw-vet-change>Change</button>' +
      '</div>' +
      '<p class="baw-address-confirm-text">' +
      '<span class="baw-vet-confirm-name">' + escapeHtml(name) + '</span><br>' +
      formatAddressConfirmHtml(address) +
      '</p>' +
      '</div>'
    )
  }

  function buildVetClinicFieldHtml(state, config) {
    var selectedId = String(state.transport.vetClinicId || '')
    var hasSelection =
      !!String(state.transport.vetClinic || '').trim() &&
      !!String(state.transport.vetAddress || '').trim() &&
      !state.transport.vetClinicEditing
    // Confirmed clinic: collapse partner pills + Places search; Change expands both again.
    var showPicker = !hasSelection
    var searchValue = state.transport.vetClinicEditing
      ? String(state.transport.vetClinic || '')
      : ''
    var partnersHtml = partnerVetClinicsForState(state).map(function (clinic) {
      var selected = selectedId === clinic.id && hasSelection && !state.transport.vetClinicOutOfArea
      return (
        '<button type="button" class="baw-vet-partner' + (selected ? ' is-selected' : '') + '" data-baw-vet-partner="' + escapeHtml(clinic.id) + '" aria-pressed="' + (selected ? 'true' : 'false') + '">' +
        escapeHtml(clinic.name) +
        '</button>'
      )
    }).join('')
    var outOfAreaHtml = state.transport.vetClinicOutOfArea
      ? '<p class="baw-address-error" role="alert">' + escapeHtml(VET_OUT_OF_AREA_MESSAGE) + '</p>'
      : ''
    return (
      '<div class="baw-field baw-pickup-side baw-pickup-vet' + (hasSelection ? ' is-confirmed' : '') + '">' +
      '<label class="baw-label" id="baw-t-vet-label">Which vet clinic? <span class="baw-required">*</span></label>' +
      '<div class="baw-pickup-side-body">' +
      (showPicker
        ? '<div class="baw-vet-partners" role="group" aria-labelledby="baw-t-vet-label">' +
          partnersHtml +
          '</div>' +
          '<p class="baw-field-hint baw-vet-search-hint">Or search for another clinic</p>' +
          '<input class="baw-input" id="baw-t-vet" type="text" placeholder="Start typing the clinic name" value="' + escapeHtml(searchValue) + '" autocomplete="off" aria-labelledby="baw-t-vet-label" />'
        : '') +
      buildVetClinicConfirmHtml(state) +
      outOfAreaHtml +
      '</div>' +
      '</div>'
    )
  }

  function monthKeyFromDate(date) {
    return date.getFullYear() + '-' + pad2(date.getMonth() + 1)
  }

  function parseMonthKey(key) {
    var m = String(key || '').match(/^(\d{4})-(\d{2})$/)
    if (!m) return null
    return new Date(Number(m[1]), Number(m[2]) - 1, 1)
  }

  function shiftMonthKey(key, delta) {
    var d = parseMonthKey(key)
    if (!d) return key
    d.setMonth(d.getMonth() + delta)
    return monthKeyFromDate(d)
  }

  function formatMonthTitle(key) {
    var d = parseMonthKey(key)
    if (!d) return key
    var months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
    return months[d.getMonth()] + ' ' + d.getFullYear()
  }

  function bookingCalendarBounds(rules) {
    var start = earliestBookingDate(rules)
    var end = addDaysLocal(start, rules.horizonDays || 90)
    return {
      minYmd: toYmd(start),
      maxYmd: toYmd(end),
      minMonth: monthKeyFromDate(start),
      maxMonth: monthKeyFromDate(end),
    }
  }

  function resolveCalendarMonth(state, rules) {
    var bounds = bookingCalendarBounds(rules)
    // Prefer the month the client is browsing. Do not force the selected date's
    // month on every render — that blocked next/prev after a date was chosen.
    var preferred = state.transport.calendarMonth
    if (!preferred && state.transport.date) {
      var selected = parseYmdLocal(state.transport.date)
      if (selected) preferred = monthKeyFromDate(selected)
    }
    if (!preferred) preferred = bounds.minMonth
    if (preferred < bounds.minMonth) return bounds.minMonth
    if (preferred > bounds.maxMonth) return bounds.maxMonth
    return preferred
  }

  function buildBookingCalendarHtml(selectedYmd, monthKey, rules) {
    var bounds = bookingCalendarBounds(rules)
    var monthDate = parseMonthKey(monthKey) || parseMonthKey(bounds.minMonth)
    var year = monthDate.getFullYear()
    var month = monthDate.getMonth()
    var key = monthKeyFromDate(monthDate)
    var canPrev = key > bounds.minMonth
    var canNext = key < bounds.maxMonth
    var firstDow = new Date(year, month, 1).getDay()
    var startOffset = (firstDow + 6) % 7 // Monday-first
    var daysInMonth = new Date(year, month + 1, 0).getDate()
    var todayYmd = toYmd(new Date())
    var dow = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    var totalCells = startOffset + daysInMonth
    var rowCount = Math.ceil(totalCells / 7)

    var html =
      '<div class="baw-cal" data-baw-cal>' +
      '<div class="baw-cal-head">' +
      '<button type="button" class="baw-cal-nav" data-baw-cal-prev aria-label="Previous month"' + (canPrev ? '' : ' disabled') + '>&lsaquo;</button>' +
      '<div class="baw-cal-title">' + escapeHtml(formatMonthTitle(key)) + '</div>' +
      '<button type="button" class="baw-cal-nav" data-baw-cal-next aria-label="Next month"' + (canNext ? '' : ' disabled') + '>&rsaquo;</button>' +
      '</div>' +
      '<table class="baw-cal-table" role="grid" aria-label="Choose a date">' +
      '<thead><tr>' +
      dow.map(function (d) { return '<th scope="col">' + d + '</th>' }).join('') +
      '</tr></thead><tbody>'

    var cell = 0
    var day = 1
    var r
    for (r = 0; r < rowCount; r++) {
      html += '<tr>'
      var c
      for (c = 0; c < 7; c++) {
        if (cell < startOffset || day > daysInMonth) {
          html += '<td class="baw-cal-cell is-empty"></td>'
        } else {
          var ymd = year + '-' + pad2(month + 1) + '-' + pad2(day)
          var inRange = ymd >= bounds.minYmd && ymd <= bounds.maxYmd
          var allowed = inRange && isBookingDateAllowed(ymd, rules)
          var classes = 'baw-cal-day'
          if (!allowed) classes += ' is-disabled'
          if (selectedYmd === ymd) classes += ' is-selected'
          if (ymd === todayYmd) classes += ' is-today'
          html += '<td class="baw-cal-cell">'
          if (allowed) {
            html +=
              '<button type="button" class="' + classes + '" data-baw-cal-day="' + escapeHtml(ymd) + '"' +
              (selectedYmd === ymd ? ' aria-pressed="true"' : ' aria-pressed="false"') +
              '>' + day + '</button>'
          } else {
            html += '<span class="' + classes + '">' + day + '</span>'
          }
          html += '</td>'
          day++
        }
        cell++
      }
      html += '</tr>'
    }

    html += '</tbody></table></div>'
    return html
  }

  function optionsFromProductVariants(product, keyPrefix) {
    if (!product || !Array.isArray(product.variants) || !product.variants.length) return []
    return product.variants.filter(function (v) { return v && v.available !== false }).map(function (v) {
      return {
        key: keyPrefix + ':variant:' + v.id,
        label: variantDisplayLabel(v, product.title),
        variant: v,
        source: 'product-variant',
      }
    })
  }

  function isSharedBookingFlow(config) {
    return !!(config && config.flow === 'shared')
  }

  /** True when the family selected at least one paid memorial product (not included). */
  function hasPurchasedMemorialItems(state, config) {
    if (!state) return false
    if (!config || !config.memorials || config.memorials.enabled === false) return false
    var catalog = getMemorialCatalog(state, config)
    var found = false
    catalog.categories.forEach(function (cat) {
      if (found) return
      var entry = getMemorialEntry(state, cat.key, cat.selectionMode)
      ;(entry.items || []).forEach(function (it) {
        if (it && it.productId != null && !it.included) found = true
      })
    })
    return found
  }

  function getReturnSpeedChoices(config, cremationType) {
    var product = config.products && config.products.returnSpeed
    if (!product) return []
    var hidePriority = !!(config.returnSettings && config.returnSettings.priorityWaterOnly) && cremationType !== 'water'
    return optionsFromProductVariants(product, 'return-speed').filter(function (o) {
      if (!hidePriority) return true
      return !/\bpriority\b/i.test(o.label || '')
    })
  }

  /**
   * Priority Return / express / rush — match label/key/variant like parseReturnSpeedLabel.
   */

  function isPriorityReturnChoice(choice) {
    var blob = [
      choice && choice.key,
      choice && choice.label,
      choice && choice.variant && choice.variant.title,
    ].filter(Boolean).join(' ')
    return /\b(priority|express|rush)\b/i.test(blob)
  }

  /**
   * Filter rules (display only — Shopify variants/SKUs unchanged):
   * - Vet · Regional Collection: hide Priority Return
   * - All other paths: Priority Return stays (subject to priorityWaterOnly)
   */

  function returnSpeedAllowedForState(choice, state) {
    if (!isPriorityReturnChoice(choice)) return true
    if (isVetTransport(state) && isRegionalCareArrangement(state)) return false
    return true
  }

  function getFilteredReturnSpeedChoices(config, state) {
    // Communal/shared cremation — ashes are not returned to the family.
    if (isSharedBookingFlow(config)) return []
    return getReturnSpeedChoices(config, state && state.cremationType).filter(function (o) {
      return returnSpeedAllowedForState(o, state)
    })
  }

  function applyReturnSpeedChoice(state, opt) {
    state.returnSpeed.key = opt ? opt.key : null
    state.returnSpeed.label = opt ? opt.label : null
    state.returnSpeed.variant = opt ? (opt.variant || null) : null
  }

  /**
   * True once the family is on or past `stepId` in the current step list.
   * Used so silent single-option defaults (e.g. Standard return) do not land
   * in the sidebar before that question is reached.
   */

  function hasReachedWizardStep(state, config, stepId) {
    if (!state || !state.currentStepId || !stepId) return false
    var steps = computeSteps(state, config)
    var curIdx = steps.findIndex(function (s) { return s.id === state.currentStepId })
    var targetIdx = steps.findIndex(function (s) { return s.id === stepId })
    if (curIdx < 0 || targetIdx < 0) return false
    return curIdx >= targetIdx
  }

  /**
   * Keep returnSpeed valid for the current care arrangement:
   * - clear if the current selection is no longer allowed (e.g. Priority + Vet Regional)
   * - auto-select when exactly one option remains (typically Standard) — only after
   *   the family has reached the return-speed step (not on earlier care/transport steps)
   * - leave unselected when multiple options
   */

  function reconcileReturnSpeedSelection(state, config) {
    if (isSharedBookingFlow(config)) {
      if (state.returnSpeed && state.returnSpeed.key) applyReturnSpeedChoice(state, null)
      return []
    }
    var allowed = getFilteredReturnSpeedChoices(config, state)
    var currentKey = state.returnSpeed && state.returnSpeed.key
    if (currentKey) {
      var stillValid = allowed.some(function (o) { return o.key === currentKey })
      if (!stillValid) applyReturnSpeedChoice(state, null)
    }
    if (
      hasReachedWizardStep(state, config, 'return-speed') &&
      !(state.returnSpeed && state.returnSpeed.key) &&
      allowed.length === 1
    ) {
      applyReturnSpeedChoice(state, allowed[0])
    }
    return allowed
  }

  function findReturnSpeedChoice(config, key, cremationType, state) {
    var list = state
      ? getFilteredReturnSpeedChoices(config, state)
      : getReturnSpeedChoices(config, cremationType)
    return list.find(function (o) { return o.key === key }) || null
  }

  /**
   * Match a product-variant choice by Theme Editor variant_match (id, sku, or title).
   * Exact id/sku/title win; otherwise first case-insensitive substring on title/sku.
   */

  function matchReturnMethodProductChoice(choices, matchRaw) {
    var match = String(matchRaw || '').replace(/\s+/g, ' ').trim().toLowerCase()
    if (!match || !choices || !choices.length) return null

    var i
    for (i = 0; i < choices.length; i++) {
      if (choices[i].variant && String(choices[i].variant.id) === match) return choices[i]
    }
    for (i = 0; i < choices.length; i++) {
      var skuExact = choices[i].variant && choices[i].variant.sku
      if (skuExact && String(skuExact).toLowerCase() === match) return choices[i]
    }
    for (i = 0; i < choices.length; i++) {
      if (String(choices[i].label || '').toLowerCase() === match) return choices[i]
    }
    for (i = 0; i < choices.length; i++) {
      var title = String(choices[i].label || '').toLowerCase()
      var sku = choices[i].variant && choices[i].variant.sku
        ? String(choices[i].variant.sku).toLowerCase()
        : ''
      if (title.indexOf(match) !== -1 || (sku && sku.indexOf(match) !== -1)) return choices[i]
    }
    return null
  }

  function returnMethodDisplayOverrideFromBlock(settings) {
    if (!settings) return null
    var title = settings.label != null ? String(settings.label).trim() : ''
    var descRaw = settings.description
    var hasDesc = descRaw != null && String(descRaw).trim() !== ''
    if (!title && !hasDesc) return null
    return {
      title: title || null,
      desc: hasDesc ? String(descRaw).trim() : null,
    }
  }

  function getReturnMethodChoices(config) {
    var product = config.products && config.products.returnMethod
    if (!product) return []
    var fromProduct = optionsFromProductVariants(product, 'return-method')
    var displayBlocks = config.returnMethodOptions || []
    if (!displayBlocks.length) return fromProduct

    var used = {}
    var fromDisplay = []
    displayBlocks.forEach(function (b) {
      var s = b.settings || {}
      var match =
        (s.variant_match != null && String(s.variant_match).trim()) ||
        (s.label != null && String(s.label).trim()) ||
        ''
      var opt = matchReturnMethodProductChoice(fromProduct, match)
      if (!opt || used[opt.key]) return
      // Mark matched (including hidden) so hide works and unmatched append skips them.
      used[opt.key] = true
      if (s.hidden) return
      fromDisplay.push({
        key: opt.key,
        label: opt.label,
        description: s.description || '',
        variant: opt.variant,
        sms_note: '',
        source: 'product-variant',
        block: b,
        displayOverride: returnMethodDisplayOverrideFromBlock(s),
      })
    })
    // Unmatched product variants keep appearing with auto-split titles.
    fromProduct.forEach(function (opt) {
      if (!used[opt.key]) fromDisplay.push(opt)
    })
    return fromDisplay
  }

  function findReturnMethodChoice(config, key) {
    return getReturnMethodChoices(config).find(function (o) { return o.key === key }) || null
  }

  /** If method label embeds a place ("Client Collection – Broadview"), expose it for cart attrs. */

  function locationFromReturnMethodLabel(label) {
    var parts = String(label || '').split(/\s+[–—-]\s+/)
    if (parts.length < 2) return null
    return parts.slice(1).join(' – ').trim() || null
  }

  function applyReturnMethodChoice(state, opt) {
    state.returnMethod.key = opt ? opt.key : null
    state.returnMethod.label = opt ? opt.label : null
    state.returnMethod.variant = opt ? (opt.variant || null) : null
    state.returnMethod.location = opt ? locationFromReturnMethodLabel(opt.label) : null
    state.returnMethod.locationBlockId = null
    state.returnMethod.displayOverride = opt && opt.displayOverride ? opt.displayOverride : null
  }

  /**
   * Regional Collection (South East SA) — home/vet service level with the same
   * cues as mapVetCollectionDisplay (regional / south east / south-east).
   * Bring to Lonsdale is always metro Adelaide care.
   */

  function isRegionalCareArrangement(state) {
    if (!state || !state.transport) return false
    var catId = String(state.transport.categoryId || '').toLowerCase()
    if (catId === 'lonsdale') return false
    if (catId && catId !== 'home' && catId !== 'vet') return false
    var blob = [
      state.transport.key,
      state.transport.label,
      state.transport.subLabel,
      state.transport.categoryLabel,
      state.transport.variant && state.transport.variant.title,
    ].filter(Boolean).join(' ')
    if (!blob) return false
    return /\b(regional|south[\s-]*east)\b/i.test(blob)
  }

  /**
   * Classify a return-method choice by flexible label/key/variant keywords so
   * Theme Editor titles still match. Order matters (south-east before east).
   * Returns: lonsdale | pet-universe | north-east | south-east | home-delivery | other
   */

  function classifyReturnMethodKind(choice) {
    var blob = [
      choice && choice.key,
      choice && choice.label,
      choice && choice.variant && choice.variant.title,
    ].filter(Boolean).join(' ')
    var s = String(blob).replace(/\s+/g, ' ').trim().toLowerCase()
    if (!s) return 'other'
    if (
      /home\s*deliver|deliver(?:ed)?\s+to\s+(your\s+)?home|personal\s+return|hand[\s-]?deliver/i.test(s)
    ) {
      return 'home-delivery'
    }
    if (/\b(same\s+)?south[\s-]*east\b|\bsouth\s*east\s+vet\b/i.test(s)) {
      return 'south-east'
    }
    if (/\bpet\s*universe\b|\bbroadview\b/i.test(s)) return 'pet-universe'
    if (/\bnorth[\s-]*east\b/i.test(s)) return 'north-east'
    if (/\bangel\s*ashes\b|\blonsdale\b/i.test(s)) return 'lonsdale'
    return 'other'
  }

  /**
   * Strip common Shopify prefixes so location titles can split cleanly.
   * e.g. "Collect from Angel Ashes Lonsdale" → "Angel Ashes Lonsdale"
   */

  function stripReturnMethodCollectPrefix(label) {
    return String(label || '')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/^(collect(?:ion)?\s+from|client\s+collection\s*[–—:\-]\s*|pick[\s-]*up\s+from)\s*/i, '')
      .trim()
  }

  /**
   * Auto-split a return-method variant title into tile title + muted desc.
   * Prefers ` · ` (and en/em dash), then known brand + suburb patterns.
   */

  function splitReturnMethodTitleParts(label) {
    var s = stripReturnMethodCollectPrefix(label)
    if (!s) return { title: '', desc: '' }

    var dotParts = s.split(/\s*·\s*/)
    if (dotParts.length >= 2) {
      return {
        title: dotParts[0].trim(),
        desc: dotParts.slice(1).join(' · ').replace(/\s+/g, ' ').trim(),
      }
    }

    var dashParts = s.split(/\s+[–—]\s+/)
    if (dashParts.length >= 2) {
      return {
        title: dashParts[0].trim(),
        desc: dashParts.slice(1).join(' – ').replace(/\s+/g, ' ').trim(),
      }
    }

    var angel = s.match(/^(Angel Ashes)\s+(.+)$/i)
    if (angel) return { title: 'Angel Ashes', desc: angel[2].replace(/\s+/g, ' ').trim() }

    var petU = s.match(/^(Pet Universe)\s+(.+)$/i)
    if (petU) return { title: 'Pet Universe', desc: petU[2].replace(/\s+/g, ' ').trim() }

    var northEast = s.match(/^(North East Veterinary Clinic)\s*[·,–—\-]?\s*(.*)$/i)
    if (northEast) {
      return {
        title: 'North East Veterinary Clinic',
        desc: (northEast[2] || '').replace(/\s+/g, ' ').trim(),
      }
    }

    return { title: s, desc: '' }
  }

  /**
   * Built-in calm titles when Theme Editor display overrides are absent.
   *
   * Display map (title / desc):
   *   Collect from Angel Ashes Lonsdale         → Angel Ashes / Lonsdale
   *   Pet Universe · Broadview                  → Pet Universe / Broadview
   *   Collect from Pet Universe Broadview       → Pet Universe / Broadview
   *   Collect from North East Veterinary Clinic → North East Veterinary Clinic / —
   *   Collect from Same South-East Vet          → Your South East Vet / —
   *   Delivered to your home / home deliver     → Home Delivery / Metro Adelaide
   */

  function defaultReturnMethodDisplay(label, kind) {
    if (kind === 'home-delivery') return { title: 'Home Delivery', desc: 'Metro Adelaide' }
    if (kind === 'south-east') return { title: 'Your South East Vet', desc: '' }

    var parts = splitReturnMethodTitleParts(label)

    if (kind === 'lonsdale') {
      return {
        title: /^angel ashes$/i.test(parts.title) ? 'Angel Ashes' : (parts.desc ? parts.title : 'Angel Ashes'),
        desc: parts.desc || 'Lonsdale',
      }
    }
    if (kind === 'pet-universe') {
      return {
        title: /^pet universe$/i.test(parts.title) || !parts.desc ? 'Pet Universe' : parts.title,
        desc: parts.desc || 'Broadview',
      }
    }
    if (kind === 'north-east') {
      return {
        title: parts.title || 'North East Veterinary Clinic',
        desc: parts.desc || '',
      }
    }
    if (parts.title) return parts
    var raw = String(label || '').replace(/\s+/g, ' ').trim()
    return { title: raw, desc: '' }
  }

  /**
   * Customer-facing return-method tile / summary copy.
   * Cart / ops keep the fuller Shopify label on state.returnMethod.label.
   * Theme Editor return_method_option blocks may override title / description.
   */

  function mapReturnMethodDisplay(label, choice) {
    var raw = String(label || (choice && choice.label) || '').replace(/\s+/g, ' ').trim()
    var kind = classifyReturnMethodKind(choice || { label: raw })
    var auto = defaultReturnMethodDisplay(raw, kind)
    var ov = choice && choice.displayOverride
    if (!ov) return auto
    return {
      title: (ov.title != null && String(ov.title).trim()) ? String(ov.title).trim() : auto.title,
      desc: ov.desc != null ? String(ov.desc).trim() : auto.desc,
    }
  }

  /**
   * Filter rules:
   * - Regional care (home/vet + regional/south-east service): only Your South East Vet
   * - Metro Adelaide care (Lonsdale / Home / metro Vet): Lonsdale, Pet Universe,
   *   North East Vet, Home Delivery — hide South East Vet
   */

  function returnMethodAllowedForState(choice, state) {
    var kind = classifyReturnMethodKind(choice)
    if (isRegionalCareArrangement(state)) return kind === 'south-east'
    if (kind === 'south-east') return false
    // Known metro options + any unrecognised Theme Editor option (forward-compatible)
    return true
  }

  function getFilteredReturnMethodChoices(config, state) {
    if (isSharedBookingFlow(config) && !hasPurchasedMemorialItems(state, config)) return []
    return getReturnMethodChoices(config).filter(function (o) {
      return returnMethodAllowedForState(o, state)
    })
  }

  /**
   * Keep returnMethod valid for the current care arrangement:
   * - clear if the current selection is no longer allowed
   * - auto-select when exactly one option remains — only after the family has
   *   reached the return-method step (avoids Ashes return in the sidebar early)
   * - leave unselected when multiple options (no preselect)
   */

  function reconcileReturnMethodSelection(state, config) {
    if (isSharedBookingFlow(config) && !hasPurchasedMemorialItems(state, config)) {
      if (state.returnMethod && state.returnMethod.key) applyReturnMethodChoice(state, null)
      return []
    }
    var allowed = getFilteredReturnMethodChoices(config, state)
    var currentKey = state.returnMethod && state.returnMethod.key
    if (currentKey) {
      var stillValid = allowed.some(function (o) { return o.key === currentKey })
      if (!stillValid) applyReturnMethodChoice(state, null)
    }
    if (
      hasReachedWizardStep(state, config, 'return-method') &&
      !(state.returnMethod && state.returnMethod.key) &&
      allowed.length === 1
    ) {
      applyReturnMethodChoice(state, allowed[0])
    }
    return allowed
  }

  function computeSteps(state, config) {
    var steps = [{ id: 'pet' }]

    if (config.flow === 'individual') {
      steps.push({ id: 'cremation-type' })
    }
    if (needsManualWeightPick(state, config)) {
      steps.push({ id: 'cremation-weight' })
    }

    var transportCat = findTransportCategory(config, state.transport.categoryId)
    if (needsTransportCategoryStep(config)) {
      steps.push({ id: 'transport-category' })
    }
    if (transportCat && categoryNeedsServiceStep(transportCat)) {
      steps.push({ id: 'transport-main' })
    }
    if (state.transport.key) {
      var leaf = transportLeaf(state, config)
      if (leaf && (leaf.requires_date || leaf.requires_time_window || leaf.requires_vet_clinic || leaf.requires_address_choice)) {
        steps.push({ id: 'transport-details' })
      }
    }

    if (config.memorials && config.memorials.enabled !== false) {
      var memorialCatalog = getMemorialCatalog(state, config)
      memorialCatalog.categories.forEach(function (cat) {
        steps.push({ id: 'memorial:' + cat.key, groupKey: cat.key })
        var entry = getMemorialEntry(state, cat.key, cat.selectionMode)
        // Sequential personalisation per selected vessel (not one shared step for the category).
        ;(entry.items || []).forEach(function (it) {
          if (it.included) return
          var addonFlags = resolveMemorialItemAddonFlags(config, cat.key, it, memorialCatalog)
          it.needsPhoto = addonFlags.needsPhoto
          it.needsNameplate = addonFlags.needsNameplate
          it.needsEngraving = addonFlags.needsEngraving
          it.needsNameplateText = addonFlags.needsNameplateText
          it.nameplateIncluded = addonFlags.nameplateIncluded
          it.photoFeeProduct = addonFlags.photoFeeProduct
          it.engravingFeeProduct = addonFlags.engravingFeeProduct
          it.nameplateProductGold = addonFlags.nameplateProductGold
          it.nameplateProductSilver = addonFlags.nameplateProductSilver
          it.nameplateGoldLabel = addonFlags.nameplateGoldLabel
          it.nameplateSilverLabel = addonFlags.nameplateSilverLabel
          it.nameplatePreviewImageGold = addonFlags.nameplatePreviewImageGold
          it.nameplatePreviewImageSilver = addonFlags.nameplatePreviewImageSilver
          it.personaliseImage = addonFlags.personaliseImage || null
          it.personaliseImageGold = addonFlags.personaliseImageGold || null
          it.personaliseImageSilver = addonFlags.personaliseImageSilver || null
          it.reviewBlankCardImage = addonFlags.reviewBlankCardImage || null
          it.photoMockupFrame = addonFlags.photoMockupFrame || null
          it.photoCardTitle = addonFlags.photoCardTitle
          it.photoCardSubtitle = addonFlags.photoCardSubtitle
          it.engravingCardTitle = addonFlags.engravingCardTitle
          it.engravingCardSubtitle = addonFlags.engravingCardSubtitle
          var pid = it.productId
          // Canonical funnel: photo first, then nameplate, then engraving. No TE order toggle.
          if (addonFlags.needsPhoto) {
            steps.push({
              id: memorialAddonStepId(cat.key, 'photo', pid),
              groupKey: cat.key,
              productId: pid,
              addonType: 'photo',
            })
          }
          if (addonFlags.needsNameplate) {
            steps.push({
              id: memorialAddonStepId(cat.key, 'nameplate', pid),
              groupKey: cat.key,
              productId: pid,
              addonType: 'nameplate',
            })
          }
          if (addonFlags.needsEngraving) {
            steps.push({
              id: memorialAddonStepId(cat.key, 'engraving', pid),
              groupKey: cat.key,
              productId: pid,
              addonType: 'engraving',
            })
          }
        })
      })
    }

    if (getFilteredReturnSpeedChoices(config, state).length) steps.push({ id: 'return-speed' })
    if (getFilteredReturnMethodChoices(config, state).length) steps.push({ id: 'return-method' })

    steps.push({ id: 'review' })
    return steps
  }

  var STEP_LABELS = {
    'pet': 'Your pet',
    'cremation-type': 'Cremation',
    'cremation-weight': 'Cremation',
    'transport-category': 'Care Arrangements',
    'transport-main': 'Care Arrangements',
    'transport-sub': 'Care Arrangements',
    'transport-details': 'Care Arrangements',
    'return-speed': 'Ashes return',
    'return-method': 'Ashes return',
    'review': 'Review',
  }

  function stepLabel(stepId, config) {
    if (isSharedBookingFlow(config) && stepId === 'return-method') {
      return 'Memorial delivery'
    }
    if (STEP_LABELS[stepId]) return STEP_LABELS[stepId]
    if (stepId.indexOf('memorial:') === 0) return 'Keepsakes'
    return ''
  }

  /** Section key for progress chrome — consecutive screens sharing a label count as one step. */

  function stepSectionKey(stepId, config) {
    return stepLabel(stepId, config) || String(stepId || '')
  }

  /**
   * Progress UI counts section categories, not raw wizard screens.
   * e.g. transport-category → transport-main → transport-details all stay "Care Arrangements".
   */

  function computeSectionProgress(steps, stepIdx, config) {
    var keys = []
    var last = null
    var current = 0
    for (var i = 0; i < steps.length; i++) {
      var key = stepSectionKey(steps[i].id, config)
      if (key !== last) {
        keys.push(key)
        last = key
      }
      if (i === stepIdx) current = keys.length - 1
    }
    return { total: Math.max(keys.length, 1), current: current }
  }

  /* ------------------------------ ledger ---------------------------------- */

  function mapVetCollectionDisplay(label, opts) {
    opts = opts || {}
    var raw = String(label || '').replace(/\s+/g, ' ').trim()
    if (!raw) return null
    var s = raw.toLowerCase()
    var forceVet = !!opts.forceVet
    var hasMetro = /\b(adelaide|metro)\b/i.test(s)
    var hasRegional = /\b(regional|south[\s-]*east)\b/i.test(s)
    var isAfterHours = /\bafter[\s-]*hours?\b/i.test(s)
    var isPriority =
      /\b(priority|express|urgent)\b/i.test(s) ||
      /\b(?:within\s+)?48\s*(?:hrs?|hours?)\b/i.test(s)
    var isStandard =
      /\bstandard\b/i.test(s) ||
      (/\bweekdays?\b/i.test(s) && !isAfterHours && !isPriority && !hasRegional)
    var namedCollection =
      /\b(standard|priority|regional)\s+collection\b/i.test(s)

    // Without forceVet, require metro / regional / already-renamed cues so Lonsdale
    // tiles (and non-collection labels) keep their existing title/subtitle parsing.
    // Home Collection uses mapHomeCollectionDisplay instead.
    if (!forceVet && !hasMetro && !hasRegional && !namedCollection) return null

    // After Hours is not a vet service level (filtered from options; home-only).
    if (isAfterHours) return null

    if (hasRegional || (forceVet && /\bregional\b/i.test(s))) {
      return { title: 'Regional Collection', desc: 'South East SA\nCollected within 7 days' }
    }
    if (isPriority) {
      if (forceVet || hasMetro || /\bpriority\s+collection\b/i.test(s) || /\b48\b/.test(s)) {
        return {
          title: 'Priority Collection',
          desc: 'Metro Adelaide\nCollected within 2 working days',
        }
      }
      return null
    }
    if (isStandard && (forceVet || hasMetro || /\bstandard\s+collection\b/i.test(s))) {
      return {
        title: 'Standard Collection',
        desc: 'Metro Adelaide\nCollected within 7 days',
      }
    }
    return null
  }

  /**
   * Customer-facing Home Collection tile copy (same service-first pattern as Vet).
   * Shopify variant titles may still say “Adelaide · Weekdays…” / “After hours” —
   * cart / ops keep those. Family-facing After Hours → After Hours and Public Holiday Collection.
   * Returns { title, desc } or null when the label is not a known home service level.
   * Multi-line desc uses \n; .baw-tile-desc has white-space: pre-line.
   *
   * Old → display:
   *   Adelaide · Weekdays / Standard…  → Standard Collection  · Metro Adelaide / Scheduled weekday collection · 10am–4pm
   *   Adelaide · After hours…          → After Hours and Public Holiday Collection · Metro Adelaide / Today, weekends & public holidays
   */

  function mapHomeCollectionDisplay(label, opts) {
    opts = opts || {}
    var raw = String(label || '').replace(/\s+/g, ' ').trim()
    if (!raw) return null
    var s = raw.toLowerCase()
    var forceHome = !!opts.forceHome
    var hasMetro = /\b(adelaide|metro)\b/i.test(s)
    var isAfterHours =
      /\bafter[\s-]*hours?\b/i.test(s) || /\bsame[\s-]*day\b/i.test(s)
    var isPriority =
      /\b(priority|express|urgent)\b/i.test(s) ||
      /\b(?:within\s+)?48\s*(?:hrs?|hours?)\b/i.test(s)
    var isStandard =
      /\bstandard\b/i.test(s) ||
      (/\bweekdays?\b/i.test(s) && !isAfterHours && !isPriority)
    var namedCollection =
      /\b(standard|after[\s-]*hours?|same[\s-]*day)\s+collection\b/i.test(s)

    // Without forceHome, require metro / already-renamed cues so Vet Priority /
    // Regional and Lonsdale tiles are not rewritten here.
    if (!forceHome && !hasMetro && !namedCollection) return null

    // Home path: Priority is vet-only — never surface as Priority Collection here.
    if (isPriority && !isAfterHours && forceHome) return null

    if (isAfterHours) {
      if (
        forceHome ||
        hasMetro ||
        /\bafter[\s-]*hours?\s+collection\b/i.test(s) ||
        /\bsame[\s-]*day\s+collection\b/i.test(s)
      ) {
        return {
          title: 'After Hours and Public Holiday Collection',
          desc: 'Metro Adelaide\nToday, weekends & public holidays',
        }
      }
      return null
    }
    if (isStandard && (forceHome || hasMetro || /\bstandard\s+collection\b/i.test(s))) {
      return {
        title: 'Standard Collection',
        desc: 'Metro Adelaide\nScheduled weekday collection · 10am–4pm',
      }
    }
    return null
  }

  /**
   * Tile display: title on line 1, hours/days/duration as subtitle (no brackets),
   * matching After Hours (“Adelaide · After hours” → After Hours and Public Holiday Collection).
   * e.g. “Adelaide · Weekdays (10am-5pm)” → title + “10am - 5pm”.
   * Vet / Home: cart / note attributes still use the full Shopify variant title.
   * Lonsdale Goodbye Room: family-facing titles also flow into Collection Type notes.
   * Pass { forceVet: true } / { forceHome: true } / { forceLonsdale: true } on the matching service step.
   */

  function formatTransportTileParts(label, opts) {
    opts = opts || {}
    // Prefer the step-specific mapper so Home/Vet/Lonsdale never steal each other's tiles.
    if (opts.forceLonsdale) {
      var lonsdaleForced = mapLonsdaleGoodbyeDisplay(label, opts)
      if (lonsdaleForced) return lonsdaleForced
    }
    if (opts.forceHome) {
      var homeForced = mapHomeCollectionDisplay(label, opts)
      if (homeForced) return homeForced
      // Home path only — skip Vet Priority/Regional remaps; fall through to generic formatter.
    } else {
      if (opts.forceVet) {
        var vetForced = mapVetCollectionDisplay(label, opts)
        if (vetForced) return vetForced
      }
      var lonsdaleMapped = mapLonsdaleGoodbyeDisplay(label, opts)
      if (lonsdaleMapped) return lonsdaleMapped
      var vetMapped = mapVetCollectionDisplay(label, opts)
      if (vetMapped) return vetMapped
      var homeMapped = mapHomeCollectionDisplay(label, opts)
      if (homeMapped) return homeMapped
    }

    var raw = String(label || '').replace(/\s+/g, ' ').trim()
    if (!raw) return { title: '', desc: '' }
    var isAfterHours = /\bafter[\s-]*hours?\b/i.test(raw) || /\bsame[\s-]*day\b/i.test(raw)
    var hint = ''

    function normalizeTileHint(text, asTime) {
      var s = String(text || '').replace(/\s+/g, ' ').trim()
      if (!s) return ''
      if (asTime) {
        return s.replace(/\s*[–—-]\s*/g, ' - ').replace(/\s+/g, ' ').trim()
      }
      return s
        .replace(/\s*[–—-]\s*/g, '–')
        .replace(/\bminutes?\b/gi, 'min')
        .replace(/\s+/g, ' ')
        .trim()
    }

    function isScheduleHint(text) {
      var s = String(text || '').trim()
      if (!s) return false
      // Time windows: 10am-5pm, After 5pm, 9:00am – 12pm
      if (/\d\s*(?:am|pm)\b|(?:am|pm)\b.*\d|\b\d{1,2}:\d{2}\b/i.test(s)) return 'time'
      // Day ranges: Mon–Fri, weekends, weekdays
      if (/\b(mon|tue|wed|thu|fri|sat|sun|weekday|weekend)/i.test(s)) return 'days'
      // Durations in parens: 5 minutes, 30 min, 48 hours
      if (/^\d+\s*(?:min|mins|minutes|hrs?|hours?)$/i.test(s)) return 'duration'
      return false
    }

    var title = raw.replace(/\(([^)]*)\)/g, function (_m, inner) {
      var kind = isScheduleHint(inner)
      if (kind) {
        hint = normalizeTileHint(inner, kind === 'time')
        return ''
      }
      return '(' + inner + ')'
    })

    title = title
      .replace(/\s+/g, ' ')
      .replace(/\s*·\s*$/g, '')
      .replace(/^\s*·\s*/g, '')
      .replace(/\bminutes?\b/gi, 'min')
      // Keep “After hours” / Same-Day intact — only shorten standalone “48 hours” style phrases
      .replace(/\b(\d+)\s*hours?\b/gi, '$1 hrs')
      .trim()

    // Trailing “· 5 min” / “· 30 minutes” (common on Lonsdale goodbye variants)
    if (!hint && !isAfterHours) {
      var durationTrail = title.match(/^(.*?)\s*·\s*(\d+\s*(?:min|mins|minutes|hrs?|hours?))\s*$/i)
      if (durationTrail && durationTrail[1].trim()) {
        title = durationTrail[1].trim()
        hint = normalizeTileHint(durationTrail[2], false)
      }
    }

    // Trailing bare time window without brackets: “… Weekdays 10am-5pm”
    if (!hint && !isAfterHours) {
      var timeTrail = title.match(
        /^(.*?)\s+((?:after\s+)?\d{1,2}(?::\d{2})?\s*(?:am|pm)(?:\s*[–—-]\s*\d{1,2}(?::\d{2})?\s*(?:am|pm))?)\s*$/i
      )
      if (timeTrail && timeTrail[1].trim()) {
        title = timeTrail[1].replace(/\s*·\s*$/g, '').trim()
        hint = normalizeTileHint(timeTrail[2], true)
      }
    }

    if (isAfterHours) {
      // Keep to one line on narrow mobile tiles (price sits on the right).
      hint = 'Today, weekends & public holidays'
      title = 'After Hours and Public Holiday Collection'
    }

    return { title: title || raw, desc: hint }
  }

  function compactTransportChoice(label, opts) {
    opts = opts || {}
    // Prefer friendly collection titles in the summary rail (no long subtitles).
    if (opts.forceLonsdale) {
      var lonsdaleForced = mapLonsdaleGoodbyeDisplay(label, opts)
      if (lonsdaleForced) {
        return lonsdaleForced.desc
          ? lonsdaleForced.title + ' · ' + lonsdaleForced.desc
          : lonsdaleForced.title
      }
    }
    if (opts.forceHome) {
      var homeForced = mapHomeCollectionDisplay(label, opts)
      if (homeForced) return homeForced.title
      // Home path — skip Vet Priority remaps; fall through to compactServiceLevel.
    } else {
      if (opts.forceVet) {
        var vetForced = mapVetCollectionDisplay(label, opts)
        if (vetForced) return vetForced.title
      }
      var lonsdaleMapped = mapLonsdaleGoodbyeDisplay(label, opts)
      if (lonsdaleMapped) {
        return lonsdaleMapped.desc
          ? lonsdaleMapped.title + ' · ' + lonsdaleMapped.desc
          : lonsdaleMapped.title
      }
      var vetMapped = mapVetCollectionDisplay(label, opts)
      if (vetMapped) return vetMapped.title
      var homeMapped = mapHomeCollectionDisplay(label, opts)
      if (homeMapped) return homeMapped.title
    }

    var raw = String(label || '').replace(/\s+/g, ' ').trim()
    if (!raw) return ''
    var parts = raw.split(/\s*·\s*/)
    if (parts.length >= 2) {
      var left = parts[0].trim()
      var right = parts.slice(1).join(' · ').replace(/^~\s*/, '').replace(/\s+/g, ' ').trim()
      // Lonsdale Goodbye Room: Short / Private / Extended Goodbye · N min
      if (/^short(\s+goodbye)?\b/i.test(left)) {
        var shortMins = compactServiceLevel(right).match(/\d+\s*min/i)
        return shortMins ? 'Short Goodbye · ' + shortMins[0].replace(/\s+/g, ' ') : 'Short Goodbye'
      }
      if (/^extended(\s+goodbye)?\b/i.test(left) || (/private/i.test(left) && /\b60\b/.test(right))) {
        var extMins = compactServiceLevel(right).match(/\d+\s*min/i)
        return extMins ? 'Extended Goodbye · ' + extMins[0].replace(/\s+/g, ' ') : 'Extended Goodbye'
      }
      if (/^private(\s+goodbye|\s+time)?\b/i.test(left) && /\d/.test(right)) {
        var mins = compactServiceLevel(right).match(/\d+\s*min/i)
        return mins ? 'Private Goodbye · ' + mins[0].replace(/\s+/g, ' ') : 'Private Goodbye'
      }
      // Vet / regional: “Adelaide · Standard …” → prefer the service level
      if (
        /standard|within|48|after\s*hours|same[\s-]*day|express|priority|urgent/i.test(right) ||
        /adelaide|metro|regional|south\s*east|north|area/i.test(left)
      ) {
        return compactServiceLevel(right) || compactServiceLevel(left)
      }
      return compactServiceLevel(left)
    }
    return compactServiceLevel(raw)
  }

  // Quiet rail copy only — cart / Collection Type keep the fuller transportNoteLabel.

  function parseReturnSpeedLabel(label) {
    var raw = String(label || '').replace(/\s+/g, ' ').trim()
    if (!raw) return { title: '', daysShort: '', isPriority: false }
    var isPriority = /\b(priority|express|rush)\b/i.test(raw)
    var title = isPriority
      ? 'Priority'
      : (/\bstandard\b/i.test(raw) ? 'Standard' : raw.split('|')[0].trim())
    var daysMatch = raw.match(/(\d+\s*[–\-]\s*\d+|\d+)\s*(working\s*)?days?/i)
    var daysShort = ''
    if (daysMatch) {
      var num = daysMatch[1].replace(/\s+/g, '').replace(/-/g, '–')
      // Priority: prefer a short range (e.g. 3 → 3–5) so families get a clear promise with a little leeway
      if (isPriority && /^\d+$/.test(num)) {
        var n = parseInt(num, 10)
        num = n + '\u2013' + (n + 2)
      }
      daysShort = num + ' days'
    } else if (isPriority) {
      daysShort = '3\u20135 days'
    }
    return { title: title, daysShort: daysShort, isPriority: isPriority }
  }

  /**
   * Min/max return SLA days from the selected product label.
   * Uses existing Standard 10–14 / Priority 3–5 ranges already shown in the wizard.
   */

    return {
      transportCategoryDef: transportCategoryDef,
      variantDisplayLabel: variantDisplayLabel,
      mapLonsdaleGoodbyeDisplay: mapLonsdaleGoodbyeDisplay,
      lonsdaleGoodbyeOptionLabel: lonsdaleGoodbyeOptionLabel,
      isAfterHoursOptionLabel: isAfterHoursOptionLabel,
      isPriorityOptionLabel: isPriorityOptionLabel,
      findAfterHoursTransportOption: findAfterHoursTransportOption,
      findPriorityTransportOption: findPriorityTransportOption,
      serviceOptionsFromProduct: serviceOptionsFromProduct,
      isLegacyLonsdaleDropOffLabel: isLegacyLonsdaleDropOffLabel,
      normalizeLonsdaleFamilyLabel: normalizeLonsdaleFamilyLabel,
      transportCategoryLabel: transportCategoryLabel,
      familyFacingTransportCategoryLabel: familyFacingTransportCategoryLabel,
      lonsdaleServiceSummaryLabel: lonsdaleServiceSummaryLabel,
      resolveTransportSmsNote: resolveTransportSmsNote,
      buildTransportCategories: buildTransportCategories,
      getTransportCategories: getTransportCategories,
      findTransportCategory: findTransportCategory,
      optionsForTransportCategory: optionsForTransportCategory,
      getTransportMainOptions: getTransportMainOptions,
      needsTransportCategoryStep: needsTransportCategoryStep,
      categoryNeedsServiceStep: categoryNeedsServiceStep,
      categoryTilePriceLabel: categoryTilePriceLabel,
      findTransportMainOption: findTransportMainOption,
      requirementsForTransportState: requirementsForTransportState,
      transportLeaf: transportLeaf,
      centsPriceLabel: centsPriceLabel,
      formatPickupAddress: formatPickupAddress,
      formatSummaryAddress: formatSummaryAddress,
      formatAddressConfirmHtml: formatAddressConfirmHtml,
      streetLineHasNumberOrUnit: streetLineHasNumberOrUnit,
      formattedAddressHasStreetNumberOrUnit: formattedAddressHasStreetNumberOrUnit,
      addressComponentsHaveStreetNumberOrUnit: addressComponentsHaveStreetNumberOrUnit,
      placeHasStreetNumberOrUnit: placeHasStreetNumberOrUnit,
      isHomeAddressInvalid: isHomeAddressInvalid,
      isHomeAddressCollapsed: isHomeAddressCollapsed,
      parseServiceAreaPolygon: parseServiceAreaPolygon,
      hasHomeServiceArea: hasHomeServiceArea,
      pointInPolygon: pointInPolygon,
      isHomeAddressInServiceArea: isHomeAddressInServiceArea,
      clearPickupAddressState: clearPickupAddressState,
      isPickupAddressReady: isPickupAddressReady,
      pad2: pad2,
      toYmd: toYmd,
      parseYmdLocal: parseYmdLocal,
      addDaysLocal: addDaysLocal,
      addWorkingDaysLocal: addWorkingDaysLocal,
      parseFlexibleDateLocal: parseFlexibleDateLocal,
      parseClosedDateLines: parseClosedDateLines,
      isAfterHoursTransport: isAfterHoursTransport,
      reconcileVetAfterHoursSelection: reconcileVetAfterHoursSelection,
      isHomeNonAfterHoursTransport: isHomeNonAfterHoursTransport,
      parseSameDayCutoffHour: parseSameDayCutoffHour,
      adelaideDateTimeParts: adelaideDateTimeParts,
      parsePreferredTimeLabelMinutes: parsePreferredTimeLabelMinutes,
      filterArrivalTimeWindowsForDate: filterArrivalTimeWindowsForDate,
      bookableArrivalTimeWindowsForState: bookableArrivalTimeWindowsForState,
      reconcileArrivalTimeWindowSelection: reconcileArrivalTimeWindowSelection,
      isAdelaideTodayYmd: isAdelaideTodayYmd,
      isPastSameDayCutoff: isPastSameDayCutoff,
      isSameDayBlockedByCutoff: isSameDayBlockedByCutoff,
      sameDayCutoffHourForState: sameDayCutoffHourForState,
      formatSameDayCutoffHourLabel: formatSameDayCutoffHourLabel,
      sameDayCutoffHintForState: sameDayCutoffHintForState,
      categoryOffersAfterHours: categoryOffersAfterHours,
      sameDayCutoffHintHtml: sameDayCutoffHintHtml,
      bookingDateRules: bookingDateRules,
      isBookingDateAllowed: isBookingDateAllowed,
      earliestBookingDate: earliestBookingDate,
      buildBookableDates: buildBookableDates,
      formatAuDateDmy: formatAuDateDmy,
      formatBookableDateLabel: formatBookableDateLabel,
      formatCollectionDateShort: formatCollectionDateShort,
      formatCollectionDateRange: formatCollectionDateRange,
      formatSummaryDateLabel: formatSummaryDateLabel,
      formatSummaryDateRange: formatSummaryDateRange,
      petFirstNameRef: petFirstNameRef,
      collectionEstimateRangeForState: collectionEstimateRangeForState,
      collectionInCareEmailHelper: collectionInCareEmailHelper,
      collectionInCareEmailSubheadingHtml: collectionInCareEmailSubheadingHtml,
      buildPickupAddressConfirmHtml: buildPickupAddressConfirmHtml,
      buildPickupAddressFieldHtml: buildPickupAddressFieldHtml,
      partnerVetClinicsForState: partnerVetClinicsForState,
      matchingSearchableVetClinics: matchingSearchableVetClinics,
      partnerVetClinicById: partnerVetClinicById,
      clearVetClinicState: clearVetClinicState,
      isVetClinicReady: isVetClinicReady,
      isVetClinicInServiceArea: isVetClinicInServiceArea,
      buildVetClinicConfirmHtml: buildVetClinicConfirmHtml,
      buildVetClinicFieldHtml: buildVetClinicFieldHtml,
      monthKeyFromDate: monthKeyFromDate,
      parseMonthKey: parseMonthKey,
      shiftMonthKey: shiftMonthKey,
      formatMonthTitle: formatMonthTitle,
      bookingCalendarBounds: bookingCalendarBounds,
      resolveCalendarMonth: resolveCalendarMonth,
      buildBookingCalendarHtml: buildBookingCalendarHtml,
      optionsFromProductVariants: optionsFromProductVariants,
      getReturnSpeedChoices: getReturnSpeedChoices,
      isPriorityReturnChoice: isPriorityReturnChoice,
      returnSpeedAllowedForState: returnSpeedAllowedForState,
      getFilteredReturnSpeedChoices: getFilteredReturnSpeedChoices,
      applyReturnSpeedChoice: applyReturnSpeedChoice,
      hasReachedWizardStep: hasReachedWizardStep,
      reconcileReturnSpeedSelection: reconcileReturnSpeedSelection,
      findReturnSpeedChoice: findReturnSpeedChoice,
      matchReturnMethodProductChoice: matchReturnMethodProductChoice,
      returnMethodDisplayOverrideFromBlock: returnMethodDisplayOverrideFromBlock,
      getReturnMethodChoices: getReturnMethodChoices,
      findReturnMethodChoice: findReturnMethodChoice,
      locationFromReturnMethodLabel: locationFromReturnMethodLabel,
      applyReturnMethodChoice: applyReturnMethodChoice,
      isRegionalCareArrangement: isRegionalCareArrangement,
      classifyReturnMethodKind: classifyReturnMethodKind,
      stripReturnMethodCollectPrefix: stripReturnMethodCollectPrefix,
      splitReturnMethodTitleParts: splitReturnMethodTitleParts,
      defaultReturnMethodDisplay: defaultReturnMethodDisplay,
      mapReturnMethodDisplay: mapReturnMethodDisplay,
      returnMethodAllowedForState: returnMethodAllowedForState,
      getFilteredReturnMethodChoices: getFilteredReturnMethodChoices,
      reconcileReturnMethodSelection: reconcileReturnMethodSelection,
      hasPurchasedMemorialItems: hasPurchasedMemorialItems,
      computeSteps: computeSteps,
      stepLabel: stepLabel,
      stepSectionKey: stepSectionKey,
      computeSectionProgress: computeSectionProgress,
      mapVetCollectionDisplay: mapVetCollectionDisplay,
      mapHomeCollectionDisplay: mapHomeCollectionDisplay,
      formatTransportTileParts: formatTransportTileParts,
      compactTransportChoice: compactTransportChoice,
      parseReturnSpeedLabel: parseReturnSpeedLabel,
      LONSDALE_FAMILY_LABEL: LONSDALE_FAMILY_LABEL,
      LONSDALE_PRODUCT_DISPLAY_TITLE: LONSDALE_PRODUCT_DISPLAY_TITLE,
      LONSDALE_SERVICE_HEADLINE: LONSDALE_SERVICE_HEADLINE,
      TRANSPORT_CATEGORY_DEFS: TRANSPORT_CATEGORY_DEFS
    }
  }

  var INSTALL_SRC = `    function loadGooglePlaces(apiKey, cb) {
      if (!apiKey) return
      if (window.google && window.google.maps && window.google.maps.places) {
        cb()
        return
      }
      if (!window.__bawPlacesQueue) window.__bawPlacesQueue = []
      window.__bawPlacesQueue.push(cb)
      if (window.__bawPlacesLoading) return
      window.__bawPlacesLoading = true
      var script = document.createElement('script')
      script.src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(apiKey) + '&libraries=places'
      script.async = true
      script.onload = function () {
        window.__bawPlacesLoading = false
        var queue = window.__bawPlacesQueue || []
        window.__bawPlacesQueue = []
        queue.forEach(function (fn) {
          try { fn() } catch (err) { /* ignore Places init errors */ }
        })
      }
      script.onerror = function () {
        window.__bawPlacesLoading = false
        window.__bawPlacesQueue = []
      }
      document.head.appendChild(script)
    }

    function adelaidePlacesBounds() {
      return new google.maps.LatLngBounds(
        new google.maps.LatLng(-35.35, 138.32),
        new google.maps.LatLng(-34.55, 139.05)
      )
    }

    /** Limestone Coast / South East SA bias for Regional Collection clinic search. */

    function southEastPlacesBounds() {
      return new google.maps.LatLngBounds(
        new google.maps.LatLng(-38.15, 139.85),
        new google.maps.LatLng(-36.85, 141.15)
      )
    }

    function attachPickupAddressAutocomplete(input, onValidate) {
      if (!input || !config.googleMapsApiKey) return
      loadGooglePlaces(config.googleMapsApiKey, function () {
        if (!input.isConnected) return
        if (!window.google || !google.maps || !google.maps.places) return
        if (input.getAttribute('data-baw-places') === '1') return
        input.setAttribute('data-baw-places', '1')
        var ac = new google.maps.places.Autocomplete(input, {
          fields: ['formatted_address', 'geometry', 'address_components'],
          types: ['address'],
          componentRestrictions: { country: 'au' },
          bounds: adelaidePlacesBounds(),
          strictBounds: false,
        })
        ac.addListener('place_changed', function () {
          var place = ac.getPlace()
          if (place && place.formatted_address) {
            var cleaned = formatPickupAddress(place.formatted_address)
            input.value = cleaned
            state.transport.newAddress = cleaned
            state.transport.addressEditing = false
            var loc = place.geometry && place.geometry.location
            if (loc && typeof loc.lat === 'function') {
              state.transport.newAddressLat = loc.lat()
              state.transport.newAddressLng = loc.lng()
            } else {
              state.transport.newAddressLat = null
              state.transport.newAddressLng = null
            }
            // Street/unit number required for home; zone check still runs with it.
            state.transport.addressMissingStreetNumber = !placeHasStreetNumberOrUnit(place)
            state.transport.addressOutOfArea = !isHomeAddressInServiceArea(
              state.transport.newAddressLat,
              state.transport.newAddressLng,
              config
            )
            if (onValidate) onValidate()
            renderSummary()
            // Places confirm collapses search — focus Change on the confirm card.
            a11yRestoreFocus = '[data-baw-address-change]'
            renderTransportDetailsStep()
          }
        })
      })
    }

    function attachVetClinicAutocomplete(input, onValidate) {
      if (!input || !config.googleMapsApiKey) return
      loadGooglePlaces(config.googleMapsApiKey, function () {
        if (!input.isConnected) return
        if (!window.google || !google.maps || !google.maps.places) return
        if (input.getAttribute('data-baw-places-vet') === '1') return
        input.setAttribute('data-baw-places-vet', '1')
        var regional = isRegionalCareArrangement(state)
        var ac = new google.maps.places.Autocomplete(input, {
          fields: ['name', 'formatted_address', 'geometry', 'place_id', 'types'],
          // Table-3 type: veterinary clinics only (not cafes / street addresses)
          types: ['veterinary_care'],
          componentRestrictions: { country: 'au' },
          bounds: regional ? southEastPlacesBounds() : adelaidePlacesBounds(),
          strictBounds: false,
        })
        var ignoreNextPlace = false

        function chooseSearchableClinic(clinic) {
          ignoreNextPlace = true
          state.transport.vetClinicId = clinic.id
          state.transport.vetClinic = clinic.name
          state.transport.vetAddress = clinic.address
          state.transport.vetClinicEditing = false
          state.transport.vetClinicOutOfArea = false
          Array.prototype.forEach.call(document.querySelectorAll('.pac-container'), function (pac) {
            pac.style.display = 'none'
          })
          if (onValidate) onValidate()
          renderSummary()
          a11yRestoreFocus = '[data-baw-vet-change]'
          renderTransportDetailsStep()
        }

        function syncSearchableClinicIntoGoogleList() {
          if (!input.isConnected) return
          var focused = document.activeElement === input
          var matches = focused ? matchingSearchableVetClinics(state, input.value) : []
          var marker = matches.map(function (clinic) { return clinic.id }).join('|')
          Array.prototype.forEach.call(document.querySelectorAll('.pac-container'), function (pac) {
            var open = pac.style.display !== 'none' && pac.offsetParent !== null
            if (!open || !focused) {
              pac.querySelectorAll('[data-baw-vet-search]').forEach(function (el) { el.remove() })
              return
            }
            var existing = Array.prototype.map.call(
              pac.querySelectorAll('[data-baw-vet-search]'),
              function (el) { return el.getAttribute('data-baw-vet-search') }
            ).join('|')
            if (existing === marker) return
            pac.querySelectorAll('[data-baw-vet-search]').forEach(function (el) { el.remove() })
            var before = pac.firstChild
            matches.forEach(function (clinic) {
              var item = document.createElement('div')
              item.className = 'pac-item baw-pac-clinic'
              item.setAttribute('data-baw-vet-search', clinic.id)
              item.setAttribute('role', 'option')
              item.innerHTML =
                '<span class="pac-icon pac-icon-marker"></span>' +
                '<span class="pac-item-query">' + escapeHtml(clinic.name) + '</span>' +
                '<span>' + escapeHtml(clinic.address) + '</span>'
              item.addEventListener('mousedown', function (event) {
                event.preventDefault()
                event.stopPropagation()
                chooseSearchableClinic(clinic)
              })
              pac.insertBefore(item, before)
            })
          })
        }

        function scheduleGoogleListSync() {
          syncSearchableClinicIntoGoogleList()
          setTimeout(syncSearchableClinicIntoGoogleList, 80)
          setTimeout(syncSearchableClinicIntoGoogleList, 250)
          Array.prototype.forEach.call(document.querySelectorAll('.pac-container'), function (pac) {
            if (pac.getAttribute('data-baw-watched') === '1') return
            pac.setAttribute('data-baw-watched', '1')
            new MutationObserver(syncSearchableClinicIntoGoogleList).observe(pac, { childList: true })
          })
        }

        input.addEventListener('input', scheduleGoogleListSync)
        input.addEventListener('focus', scheduleGoogleListSync)
        ac.addListener('place_changed', function () {
          if (ignoreNextPlace) {
            ignoreNextPlace = false
            return
          }
          var place = ac.getPlace()
          var name = place && (place.name || '').trim()
          var address = place && place.formatted_address
            ? formatPickupAddress(place.formatted_address)
            : ''
          if (!name || !address) return
          var loc = place.geometry && place.geometry.location
          var lat = loc && typeof loc.lat === 'function' ? loc.lat() : null
          var lng = loc && typeof loc.lng === 'function' ? loc.lng() : null
          state.transport.vetClinicId = null
          state.transport.vetClinic = name
          state.transport.vetAddress = address
          state.transport.vetClinicEditing = false
          state.transport.vetClinicOutOfArea = !isVetClinicInServiceArea(lat, lng, state, config)
          input.value = name
          if (onValidate) onValidate()
          renderSummary()
          // Places confirm collapses the picker — focus Change on the confirm card.
          a11yRestoreFocus = '[data-baw-vet-change]'
          renderTransportDetailsStep()
        })
      })
    }

    function clearTransportSelection() {
      state.transport.key = null
      state.transport.label = null
      state.transport.variant = null
      state.transport.subKey = null
      state.transport.subLabel = null
      state.transport.subVariant = null
      state.transport.date = null
      state.transport.calendarMonth = null
      state.transport.timeWindow = null
      clearVetClinicState(state.transport)
      clearPickupAddressState(state.transport)
    }

    function applyTransportOption(opt) {
      state.transport.key = opt.key
      state.transport.label = opt.label
      state.transport.variant = opt.variant || null
      state.transport.subKey = null
      state.transport.subLabel = null
      state.transport.subVariant = null
      state.transport.date = null
      state.transport.calendarMonth = null
      state.transport.timeWindow = null
      clearVetClinicState(state.transport)
      clearPickupAddressState(state.transport)
    }

    function optionPriceLabel(opt) {
      if (!opt || !opt.variant) return 'Included'
      var cents = Number(opt.variant.price) || 0
      return cents ? money(cents) : 'Included'
    }

    function renderTransportCategoryStep() {
      var categories = getTransportCategories(config)
      var tiles = categories.map(function (c) {
        var def = transportCategoryDef(c.id)
        return {
          id: c.id,
          title: c.label,
          desc: c.tileDesc || (def && def.tileDesc) || '',
          priceLabel: categoryTilePriceLabel(c, money),
        }
      })

      renderTileStep({
        headline: copyHeadline('transportCategoryHeadline'),
        helper: copyHeadline('transportCategorySubtitle'),
        tiles: tiles,
        selectedId: state.transport.categoryId,
        onSelect: function (id) {
          if (state.transport.categoryId !== id) {
            var cat = categories.find(function (c) { return c.id === id })
            state.transport.categoryId = id
            state.transport.categoryLabel = cat ? cat.label : id
            clearTransportSelection()
            // Single service level and no subtype step → select immediately
            if (cat && !categoryNeedsServiceStep(cat) && cat.options.length === 1) {
              applyTransportOption(cat.options[0])
            }
          }
          render()
        },
      })
    }

    function renderTransportMainStep() {
      var cat = findTransportCategory(config, state.transport.categoryId)
      var options = cat ? cat.options.slice() : getTransportMainOptions(config)

      var tiles = options.map(function (o) {
        var parts = formatTransportTileParts(o.label, {
          forceVet: !!(cat && cat.id === 'vet'),
          forceHome: !!(cat && cat.id === 'home'),
          forceLonsdale: !!(cat && cat.id === 'lonsdale'),
        })
        return {
          id: o.key,
          title: parts.title,
          desc: parts.desc,
          priceLabel: optionPriceLabel(o),
        }
      })

      var headline
      var helper = ''
      var headlineClass = ''
      var introHtml = ''
      var composeClass = ''
      var optionsClass = ''

      if (cat && cat.id === 'lonsdale') {
        // Question leads; helper explains private Goodbye Room time (DT + mobile).
        headline = copyHeadline('transportLonsdaleServiceHeadline')
        var lonsdaleSubtitle = copyHeadline(
          'transportLonsdaleServiceSubtitle',
          'Private time in our Goodbye Room before cremation care continues.'
        )
        helper = lonsdaleSubtitle
          ? '<p>' + escapeHtml(lonsdaleSubtitle) + '</p>'
          : ''
        // Soft Goodbye Room context photos (Theme Editor). Empty when unset.
        introHtml = goodbyeRoomVisualHtml(config)
        if (introHtml) composeClass = 'baw-goodbye-compose'
        // Stacked full-width rows for Short / Private / Extended — scoped CSS class.
        optionsClass = 'baw-options--goodbye-duration'
      } else if (cat && cat.id === 'vet') {
        headline = copyHeadline('transportVetServiceHeadline')
        headlineClass = 'baw-headline--compact'
      } else if (cat && cat.id === 'home') {
        headline = copyHeadline('transportHomeServiceHeadline')
      } else {
        var serviceQ = (cat && cat.serviceHeadline) || 'How should we collect {pet}?'
        headline = personalize(serviceQ, String(serviceQ).replace(/\\{pet\\}/g, 'your pet'))
      }

      renderTileStep({
        headline: headline,
        headlineClass: headlineClass,
        helper: helper,
        introHtml: introHtml,
        composeClass: composeClass,
        optionsClass: optionsClass,
        tiles: tiles,
        selectedId: state.transport.key,
        onSelect: function (id) {
          if (state.transport.key !== id) {
            var opt = findTransportMainOption(config, id)
            if (opt) applyTransportOption(opt)
          }
          render()
        },
      })
    }

    function renderTransportSubStep() {
      // Legacy transport sub-blocks removed — service levels live on transport products.
      goNext()
    }

    function renderTransportDetailsStep() {
      var leaf = transportLeaf(state, config) || {}
      var dateLabel = leaf.dateLabel || 'Preferred date'
      if (isVetTransport(state)) dateLabel = 'Ready for collection from'
      var timeLabel = leaf.timeWindowLabel || 'Preferred time window'
      var addressBesideDate =
        !!leaf.requires_date && !leaf.requires_time_window && !!leaf.requires_address_choice
      var vetBesideDate =
        !!leaf.requires_date && !leaf.requires_time_window && !!leaf.requires_vet_clinic && !leaf.requires_address_choice
      var detailsBesideDate = addressBesideDate || vetBesideDate
      // Vet/home ready-from date: reassurances anchor above Continue (not under title/calendar)
      var showPickupFooterNotes = !!leaf.requires_date && !leaf.requires_time_window

      var detailsQ = leaf.detailsHeadline || 'A few pickup details'
      var detailsHeadline = personalize(detailsQ, String(detailsQ).replace(/\\{pet\\}/g, 'your pet'))
      // Cutoff copy lives under the step heading (not under the calendar) so pickup grids stay aligned.
      var cutoffHeadingHint = sameDayCutoffHintHtml(config, state)
      var inCareSubheading =
        showPickupFooterNotes && isVetTransport(state)
          ? collectionInCareEmailSubheadingHtml(state, config)
          : ''
      var html =
        '<div class="baw-step-inner">' +
        stepIntroHtml(detailsHeadline, cutoffHeadingHint + inCareSubheading)

      if (leaf.requires_date && leaf.requires_time_window) {
        var dateRules = bookingDateRules(config, state)
        var bookableDates = buildBookableDates(dateRules)
        if (state.transport.date && bookableDates.indexOf(state.transport.date) === -1) {
          state.transport.date = null
        }
        state.transport.calendarMonth = resolveCalendarMonth(state, dateRules)
        var rawTimeOptions = timeWindowOptionsForState(state, config) || []
        var timeOptions = bookableArrivalTimeWindowsForState(state, config)
        reconcileArrivalTimeWindowSelection(state, config)
        var timesEmptyMessage =
          state.transport.date &&
          rawTimeOptions.length &&
          !timeOptions.length &&
          isAdelaideTodayYmd(state.transport.date)
            ? 'No preferred times left today. Please choose another day.'
            : null
        // Calendar + time picker are self-explanatory — no standing label above the grid.
        html +=
          '<div class="baw-field baw-schedule">' +
          '<div class="baw-schedule-grid">' +
          '<div class="baw-schedule-cal">' +
          buildBookingCalendarHtml(state.transport.date, state.transport.calendarMonth, dateRules) +
          '</div>' +
          '<div class="baw-schedule-times">' +
          '<div class="baw-schedule-times-head">Time</div>' +
          buildArrivalTimeSlotsHtml(
            state.transport.timeWindow,
            timeOptions,
            !!state.transport.date,
            timesEmptyMessage
          ) +
          '</div>' +
          '</div>' +
          '</div>'
      } else if (detailsBesideDate) {
        var dateRulesBeside = bookingDateRules(config, state)
        var bookableDatesBeside = buildBookableDates(dateRulesBeside)
        if (state.transport.date && bookableDatesBeside.indexOf(state.transport.date) === -1) {
          state.transport.date = null
        }
        state.transport.calendarMonth = resolveCalendarMonth(state, dateRulesBeside)
        // Matching label rows (date + address/clinic) keep calendar and side card top-aligned.
        // Footer strip carries email / range[+Priority] / home SMS — suppress side sms_note to avoid echo
        html +=
          '<div class="baw-field baw-pickup">' +
          '<div class="baw-pickup-grid">' +
          '<div class="baw-pickup-cal" role="group" aria-labelledby="baw-t-date-label">' +
          '<label class="baw-label" id="baw-t-date-label">' + escapeHtml(dateLabel) + ' <span class="baw-required">*</span></label>' +
          buildBookingCalendarHtml(state.transport.date, state.transport.calendarMonth, dateRulesBeside) +
          '</div>' +
          (addressBesideDate
            ? buildPickupAddressFieldHtml(
                state,
                config,
                showPickupFooterNotes ? '' : leaf.sms_note || ''
              )
            : buildVetClinicFieldHtml(state, config)) +
          '</div>' +
          '</div>'
      } else {
        if (leaf.requires_date) {
          var dateRulesSolo = bookingDateRules(config, state)
          var bookableDatesSolo = buildBookableDates(dateRulesSolo)
          if (state.transport.date && bookableDatesSolo.indexOf(state.transport.date) === -1) {
            state.transport.date = null
          }
          state.transport.calendarMonth = resolveCalendarMonth(state, dateRulesSolo)
          html +=
            '<div class="baw-field">' +
            '<label class="baw-label">' + escapeHtml(dateLabel) + ' <span class="baw-required">*</span></label>' +
            buildBookingCalendarHtml(state.transport.date, state.transport.calendarMonth, dateRulesSolo) +
            '</div>'
        }
        if (leaf.requires_time_window) {
          var rawTimeOptionsSolo = timeWindowOptionsForState(state, config) || []
          var timeOptionsSolo = bookableArrivalTimeWindowsForState(state, config)
          reconcileArrivalTimeWindowSelection(state, config)
          var timesEmptyMessageSolo =
            state.transport.date &&
            rawTimeOptionsSolo.length &&
            !timeOptionsSolo.length &&
            isAdelaideTodayYmd(state.transport.date)
              ? 'No preferred times left today. Please choose another day.'
              : null
          if (rawTimeOptionsSolo.length || timeOptionsSolo.length) {
            var timeHintSolo = timeWindowHelperForState(state, config)
            html +=
              '<div class="baw-field">' +
              '<label class="baw-label">' + escapeHtml(timeLabel) + ' <span class="baw-required">*</span></label>' +
              (timeHintSolo ? '<p class="baw-field-hint">' + escapeHtml(timeHintSolo) + '</p>' : '') +
              buildArrivalTimeSlotsHtml(
                state.transport.timeWindow,
                timeOptionsSolo,
                true,
                timesEmptyMessageSolo
              ) +
              '</div>'
          }
        }
      }
      if (leaf.requires_vet_clinic && !vetBesideDate) {
        html += buildVetClinicFieldHtml(state, config)
      }
      if (leaf.requires_address_choice && !addressBesideDate) {
        html += buildPickupAddressFieldHtml(
          state,
          config,
          showPickupFooterNotes ? '' : leaf.sms_note || ''
        )
      } else if (leaf.sms_note && !addressBesideDate && !showPickupFooterNotes) {
        // Non-home layouts: note stays below the details block (not when footer strip covers SMS)
        html += '<div class="baw-note">' + escapeHtml(leaf.sms_note) + '</div>'
      }

      if (showPickupFooterNotes) {
        // Priority cue stays vet-beside-date Standard only (same rule as before)
        html += pickupCollectionFooterNotesHtml(state, vetBesideDate, config)
      } else if (leaf.requires_date && leaf.requires_time_window && isLonsdaleTransport(state)) {
        // Arrival date + time step only (not Goodbye Room length choice)
        html += lonsdaleArrivalFooterNotesHtml(state, config)
      }

      html +=
        '<div class="baw-actions"><button type="button" class="baw-continue" data-baw-continue>Continue</button></div>' +
        '</div>'

      els.step.innerHTML = html

      // Helper CTAs: land on transport-main with Same-Day / Priority already selected.
      bindGuidanceGotoClicks(els.step)

      function validate() {
        var cleared = leaf.requires_time_window
          ? reconcileArrivalTimeWindowSelection(state, config)
          : false
        if (cleared) {
          renderTransportDetailsStep()
          return false
        }
        var ok = true
        if (leaf.requires_date && !state.transport.date) ok = false
        if (leaf.requires_time_window && !state.transport.timeWindow) ok = false
        if (leaf.requires_vet_clinic && !isVetClinicReady(state)) ok = false
        if (leaf.requires_address_choice && !isPickupAddressReady(state, config)) ok = false
        setContinueEnabled(ok)
        return ok
      }

      function clearArrivalSlotFreshnessWatch() {
        if (window.__bawArrivalSlotRefreshTimer) {
          clearInterval(window.__bawArrivalSlotRefreshTimer)
          window.__bawArrivalSlotRefreshTimer = null
        }
        if (window.__bawArrivalSlotFreshnessHandler) {
          document.removeEventListener('visibilitychange', window.__bawArrivalSlotFreshnessHandler)
          window.removeEventListener('focus', window.__bawArrivalSlotFreshnessHandler)
          window.__bawArrivalSlotFreshnessHandler = null
        }
      }

      function arrivalSlotLabelsOnScreen() {
        return Array.prototype.map.call(
          els.step.querySelectorAll('[data-baw-time]'),
          function (btn) { return btn.getAttribute('data-baw-time') }
        )
      }

      function refreshArrivalSlotsIfStale() {
        if (state.currentStepId !== 'transport-details') {
          clearArrivalSlotFreshnessWatch()
          return
        }
        if (!(leaf.requires_time_window && state.transport.date)) return
        var bookable = bookableArrivalTimeWindowsForState(state, config)
        var shown = arrivalSlotLabelsOnScreen()
        var selectionCleared = reconcileArrivalTimeWindowSelection(state, config)
        var listChanged =
          bookable.length !== shown.length ||
          bookable.some(function (t, i) { return t !== shown[i] })
        var emptyShown = !!els.step.querySelector('.baw-schedule-times-empty')
        var emptyExpected = !bookable.length
        if (selectionCleared || listChanged || emptyShown !== emptyExpected) {
          renderTransportDetailsStep()
        }
      }

      clearArrivalSlotFreshnessWatch()
      if (leaf.requires_time_window && state.transport.date) {
        window.__bawArrivalSlotFreshnessHandler = function () {
          if (document.visibilityState === 'hidden') return
          refreshArrivalSlotsIfStale()
        }
        document.addEventListener('visibilitychange', window.__bawArrivalSlotFreshnessHandler)
        window.addEventListener('focus', window.__bawArrivalSlotFreshnessHandler)
        window.__bawArrivalSlotRefreshTimer = setInterval(refreshArrivalSlotsIfStale, 30000)
      }

      var calPrev = els.step.querySelector('[data-baw-cal-prev]')
      if (calPrev) {
        calPrev.addEventListener('click', function () {
          var rules = bookingDateRules(config, state)
          var bounds = bookingCalendarBounds(rules)
          var current = state.transport.calendarMonth || resolveCalendarMonth(state, rules)
          var prev = shiftMonthKey(current, -1)
          state.transport.calendarMonth = prev < bounds.minMonth ? bounds.minMonth : prev
          a11yRestoreFocus = '[data-baw-cal-prev]'
          renderTransportDetailsStep()
        })
      }
      var calNext = els.step.querySelector('[data-baw-cal-next]')
      if (calNext) {
        calNext.addEventListener('click', function () {
          var rules = bookingDateRules(config, state)
          var bounds = bookingCalendarBounds(rules)
          var current = state.transport.calendarMonth || resolveCalendarMonth(state, rules)
          var next = shiftMonthKey(current, 1)
          state.transport.calendarMonth = next > bounds.maxMonth ? bounds.maxMonth : next
          a11yRestoreFocus = '[data-baw-cal-next]'
          renderTransportDetailsStep()
        })
      }
      els.step.querySelectorAll('[data-baw-cal-day]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          state.transport.date = btn.getAttribute('data-baw-cal-day')
          var picked = parseYmdLocal(state.transport.date)
          if (picked) state.transport.calendarMonth = monthKeyFromDate(picked)
          // Mobile: jump to the next section (time, clinic, or address)
          if (!queueMobileReveal(
            leaf.requires_date && leaf.requires_time_window
              ? '.baw-schedule-times'
              : (vetBesideDate || leaf.requires_vet_clinic)
                ? '.baw-pickup-vet'
                : (addressBesideDate || leaf.requires_address_choice)
                  ? '.baw-pickup-address'
                  : null,
            leaf.requires_date && leaf.requires_time_window
              ? '[data-baw-time]'
              : (vetBesideDate || leaf.requires_vet_clinic)
                ? '#baw-t-vet, [data-baw-vet-partner]'
                : (addressBesideDate || leaf.requires_address_choice)
                  ? '#baw-t-address'
                  : null
          )) {
            a11yRestoreFocus = '[data-baw-cal-day="' + state.transport.date + '"]'
          }
          renderTransportDetailsStep()
        })
      })
      els.step.querySelectorAll('[data-baw-time]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var picked = btn.getAttribute('data-baw-time')
          if (!picked) return
          state.transport.timeWindow = picked
          // After time, jump to clinic/address when those follow below the schedule
          if (leaf.requires_vet_clinic && !vetBesideDate) {
            queueMobileReveal('.baw-pickup-vet', '#baw-t-vet, [data-baw-vet-partner]')
          } else if (leaf.requires_address_choice && !addressBesideDate) {
            queueMobileReveal('.baw-pickup-address', '#baw-t-address')
          }
          if (!pendingMobileReveal) {
            // Prefer class selector — avoids fragile attribute escaping in INSTALL_SRC.
            a11yRestoreFocus = '[data-baw-time].is-selected'
          }
          renderTransportDetailsStep()
        })
      })
      els.step.querySelectorAll('[data-baw-vet-partner]').forEach(bindVetPartnerButton)
      function bindVetPartnerButton(btn) {
        btn.addEventListener('click', function () {
          var partner = partnerVetClinicById(btn.getAttribute('data-baw-vet-partner'))
          if (!partner) return
          state.transport.vetClinicId = partner.id
          state.transport.vetClinic = partner.name
          state.transport.vetAddress = partner.address
          state.transport.vetClinicEditing = false
          state.transport.vetClinicOutOfArea = false
          // Partner list collapses after confirm — focus the Change control.
          a11yRestoreFocus = '[data-baw-vet-change]'
          renderTransportDetailsStep()
        })
      }
      var changeVet = els.step.querySelector('[data-baw-vet-change]')
      if (changeVet) {
        changeVet.addEventListener('click', function () {
          state.transport.vetClinicEditing = true
          state.transport.vetClinicId = null
          state.transport.vetClinicOutOfArea = false
          queueMobileReveal('#baw-t-vet', '#baw-t-vet', { forTyping: true, focusDelay: 100 })
          if (!pendingMobileReveal) a11yRestoreFocus = '#baw-t-vet'
          renderTransportDetailsStep()
        })
      }
      var vetEl = els.step.querySelector('#baw-t-vet')
      if (vetEl) {
        vetEl.addEventListener('input', function () {
          // Typing clears a prior confirmation until Places (or a search match) is chosen again
          var typed = String(vetEl.value || '')
          state.transport.vetClinicId = null
          state.transport.vetClinic = typed.trim() || null
          state.transport.vetAddress = null
          state.transport.vetClinicEditing = true
          state.transport.vetClinicOutOfArea = false
          validate()
          renderSummary()
        })
        attachVetClinicAutocomplete(vetEl, validate)
      }

      var changeAddr = els.step.querySelector('[data-baw-address-change]')
      if (changeAddr) {
        changeAddr.addEventListener('click', function () {
          // Expand Places search again.
          state.transport.addressEditing = true
          state.transport.addressOutOfArea = false
          state.transport.addressMissingStreetNumber = false
          queueMobileReveal('#baw-t-address', '#baw-t-address', { forTyping: true, focusDelay: 100 })
          if (!pendingMobileReveal) a11yRestoreFocus = '#baw-t-address'
          renderTransportDetailsStep()
        })
      }
      var addrInput = els.step.querySelector('#baw-t-address')
      if (addrInput) {
        addrInput.addEventListener('input', function () {
          state.transport.newAddress = formatPickupAddress(addrInput.value)
          // Typing invalidates a prior Places confirmation / geo check
          state.transport.newAddressLat = null
          state.transport.newAddressLng = null
          state.transport.addressEditing = true
          state.transport.addressOutOfArea = false
          state.transport.addressMissingStreetNumber = false
          validate()
          renderSummary()
        })
        addrInput.addEventListener('blur', function () {
          var cleaned = formatPickupAddress(addrInput.value)
          if (cleaned !== addrInput.value) addrInput.value = cleaned
          state.transport.newAddress = cleaned
          // No polygon: typed address can confirm (Places path sets editing false itself),
          // but still require a street/unit number via formatted-address fallback.
          if (cleaned && !hasHomeServiceArea(config) && state.transport.addressEditing) {
            state.transport.addressMissingStreetNumber = !formattedAddressHasStreetNumberOrUnit(cleaned)
            state.transport.addressEditing = false
            a11yRestoreFocus = '[data-baw-address-change]'
            renderTransportDetailsStep()
            return
          }
          validate()
          renderSummary()
        })
        attachPickupAddressAutocomplete(addrInput, validate)
      }

      enhanceCalendarKeyboard(els.step.querySelector('[data-baw-cal]'))
      enhanceOptionGroup(els.step.querySelector('.baw-schedule-times-list'), '[data-baw-time]', 'Preferred arrival time')

      els.step.querySelector('[data-baw-continue]').addEventListener('click', function () {
        if (validate()) goNext()
      })
      validate()
      renderSummary()
      restoreA11yFocus()
      flushMobileReveal()
      mountStepFooter()
      // After footer is mounted, cap calendar/times to the visible step area
      scheduleSyncScheduleLayout()
    }

    function syncTimesScrollAffordances() {
      var panel = els.step && els.step.querySelector('.baw-schedule-times')
      var list = panel && panel.querySelector('.baw-schedule-times-list')
      if (!panel || !list) return

      var overflows = list.scrollHeight > list.clientHeight + 1
      var atTop = list.scrollTop <= 1
      var atBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 1

      panel.classList.toggle('is-scrollable', overflows)
      panel.classList.toggle('has-more-below', overflows && !atBottom)
      panel.classList.toggle('has-more-above', overflows && !atTop)

      if (!list.getAttribute('data-baw-scroll-bound')) {
        list.setAttribute('data-baw-scroll-bound', '1')
        list.addEventListener('scroll', function () {
          syncTimesScrollAffordances()
        }, { passive: true })
      }
    }

    function syncScheduleTimesHeight() {
      var cal = els.step.querySelector('.baw-schedule-cal .baw-cal')
      var times = els.step.querySelector('.baw-schedule-times')
      var pickupCal = els.step.querySelector('.baw-pickup-cal .baw-cal')

      function resetCal(el) {
        if (!el) return
        el.style.height = ''
        el.style.maxHeight = ''
        el.style.overflowY = ''
      }

      resetCal(cal)
      resetCal(pickupCal)
      if (times) {
        times.style.height = ''
        times.style.maxHeight = ''
        times.classList.remove('is-scrollable', 'has-more-below', 'has-more-above')
      }

      if (window.matchMedia && window.matchMedia('(max-width: 1199px)').matches) {
        syncTimesScrollAffordances()
        return
      }

      function availableHeightFrom(el) {
        if (!els.step || !el) return null
        var stepRect = els.step.getBoundingClientRect()
        var elRect = el.getBoundingClientRect()
        // Stay inside the scrollport above Continue — never let month growth clip under the footer
        return Math.floor(stepRect.bottom - elRect.top - 10)
      }

      if (cal && times) {
        // Desktop side-by-side: size to the taller panel so modest slot lists (~10–16)
        // show without scrolling; grow calendar to match. Cap only when content
        // would push under the Continue footer (extreme slot counts).
        var calNatural = cal.offsetHeight
        var list = times.querySelector('.baw-schedule-times-list')
        // Temporarily size the list to its content so offsetHeight isn't collapsed
        // by flex:1 / overflow:auto while the panel height is still auto.
        if (list) {
          list.style.flex = '0 0 auto'
          list.style.overflow = 'visible'
          list.style.minHeight = 'auto'
        }
        var timesNatural = times.offsetHeight
        if (list) {
          list.style.flex = ''
          list.style.overflow = ''
          list.style.minHeight = ''
        }
        var avail = availableHeightFrom(els.step.querySelector('.baw-schedule-grid') || cal)
        var h = Math.max(calNatural, timesNatural)
        if (avail != null && avail > 0 && h > avail) {
          h = Math.max(240, avail)
        }
        times.style.height = h + 'px'
        cal.style.height = h + 'px'
        if (calNatural > h) {
          cal.style.maxHeight = h + 'px'
          cal.style.overflowY = 'auto'
        }
      } else if (pickupCal) {
        var pNatural = pickupCal.offsetHeight
        var pAvail = availableHeightFrom(pickupCal)
        if (pAvail != null && pAvail > 0 && pNatural > pAvail) {
          pickupCal.style.maxHeight = Math.max(240, pAvail) + 'px'
          pickupCal.style.overflowY = 'auto'
        }
      }

      syncTimesScrollAffordances()
    }

    function scheduleSyncScheduleLayout() {
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(syncScheduleTimesHeight)
      })
    }

    /* ------------------------------ memorial steps ------------------------------ */`
  var INSTALL_EXPORTS = [
    "loadGooglePlaces",
    "adelaidePlacesBounds",
    "southEastPlacesBounds",
    "attachPickupAddressAutocomplete",
    "attachVetClinicAutocomplete",
    "clearTransportSelection",
    "applyTransportOption",
    "optionPriceLabel",
    "renderTransportCategoryStep",
    "renderTransportMainStep",
    "renderTransportSubStep",
    "renderTransportDetailsStep",
    "syncTimesScrollAffordances",
    "syncScheduleTimesHeight",
    "scheduleSyncScheduleLayout"
  ]

  function install(ctx) {
    var api = {}
    // Sloppy-mode runner: with (ctx) resolves free identifiers to the live
    // wizard context (state, els, render, helpers). Function declarations in
    // INSTALL_SRC become install-locals and are exported onto api.
    var body = 'with (ctx) {\n' + INSTALL_SRC + '\n'
    for (var i = 0; i < INSTALL_EXPORTS.length; i++) {
      var name = INSTALL_EXPORTS[i]
      body += 'api[' + JSON.stringify(name) + '] = ' + name + ';\n'
    }
    body += '}'
    var runner = new Function('ctx', 'api', body)
    runner(ctx, api)
    return api
  }

  global.AngelAshesBookingArrival = {
    create: create,
    install: install,
  }
})(typeof window !== 'undefined' ? window : this)
